import type {
  ReadinessResponse,
  ReadinessCheck,
  ProvisioningStatus,
  Organisation,
  Account,
  AccountBalance,
  AccountMappings,
  AccountMappingsResponse,
  JournalEntry,
  BankStatementFormat,
  QifDateFormat,
  BankStatementImportResult,
  SetupStatus,
  User,
  AdminUser,
  AdminOrganisation,
  Invitation,
  InvitationPreview,
  UsageSummaryResponse,
  UsageDailyTotalsResponse,
  UsageCurrentResponse,
  AuditLogsResponse,
  AuditLogEntry,
  SystemInfoResponse,
  ApiToken,
  ApiTokenCreateResponse,
  EmailSettings,
  StripeSettings,
  SystemSettingEntry,
  NotificationSettings,
  AnnouncementSettings,
  AnnouncementLevel,
  AnnouncementPayAttentionStyle,
  Plan,
  Subscription,
  RateLimitStatusResponse,
  BillingProfile,
  InvoiceDetail,
  InvoicesResponse,
  Customer,
  CustomerDetail,
  Supplier,
  SupplierDetail,
  Bill,
  BillDetail,
  InventoryItem,
  InventoryMovement,
  Recipe,
  MenuItem,
  SaleEvent,
  BankReconciliation,
  BankReconciliationDetail,
  RevaluationRunResponse,
  InvoiceTemplate,
  ProfitLossReport,
  ProfitLossTrendReport,
  BalanceSheetReport,
  TrialBalanceReport,
  GeneralLedgerReport,
  CashFlowSummaryReport,
  GstSummaryReport,
  ArAgingReport,
  ApAgingReport,
  PeriodCloseResult,
  PeriodCloseStatus,
  SlaCredit,
  SlaCreditsResponse,
  BrandingAsset,
  ContentReview,
  ExchangeRate,
  WalletInfo,
  WalletTransaction,
  WalletTransactionsResponse,
  CardStatus,
  CardInfo,
  SetupIntentResponse,
  CardVerificationResponse,
  CardVerifyConfirmResponse,
  Quote,
  PurchaseOrder,
  CreditNote,
  ExpenseClaim,
  ExpenseClaimDetail,
  TrackingCategory,
  BudgetData,
  JobData,
  FixedAssetData,
  BankRuleData,
  BankTransactionData,
  ContactGroupData,
  BudgetVsActualReport,
  JobProfitabilityReport,
  AccountTransactionsReport,
  ExecutiveDashboardReport,
  TimeEntry,
  TimeEntriesResponse,
  PortalDashboard,
  PortalInvoiceDetail,
  PortalQuoteDetail,
  PortalCreditNoteDetail,
  PortalPayInvoiceResponse,
  PortalSurchargePreview,
  PortalApplyCreditResponse,
  UtilityAccount,
  UtilityAccountDetail,
  UtilityRatePlan,
  UtilityReading,
  UtilityReadingDetail,
  SpotPrice,
  GridZone,
  BillProjection,
  BillReconciliation,
  FeatureFlag,
  FeatureFlagOverride,
  OrgFeaturesResponse,
  ReleaseChannel,
  GeoRestrictionSettings,
  ImportStatus,
  InboundBillCapture,
  InboundBillCaptureDetail,
  CapturesResponse,
  CaptureStatus,
  CaptureSource,
  TaxProfileResponse,
  TaxPeriodsResponse,
  TaxAdjustmentItem,
  TaxAdjustmentsResponse,
  ApportionmentProfileItem,
  ApportionmentProfilesResponse,
  IncomeTaxComputationItem,
  IncomeTaxComputationsResponse,
  ProvisionalTaxEstimateItem,
  ProvisionalTaxEstimatesResponse,
  EmployeeDeductionItem,
  EmployeeDeductionsResponse,
  DeductionCategoryItem,
  DeductionSummaryResponse,
  TaxPositionSnapshotItem,
  FilingPackItem,
  FilingPackDetailItem,
  FilingPacksResponse,
  FilingPackExport,
  TaxCalendarResponse,
} from './types'
import { OfflineStore } from '@/lib/offline-store'
import { SyncQueue } from '@/lib/sync-queue'
import { SequenceManager } from '@/lib/sequence-manager'
import { OfflineError } from '@/lib/offline-error'
import { parseToken, OFFLINE_GRACE_SECONDS, HEARTBEAT_INTERVAL_MS } from '@/lib/jwt-expiry'
import { isShowcaseMode } from '@/lib/showcase-mode'
import { showcaseRequest } from '@/lib/showcase-fixtures'
import { disconnectCable, updateCableToken, isWsReady, wsRequest, isCableReconnecting, onCableStatusChange, startWsPingLoop, stopWsPingLoop } from '@/api/cable'

// Runtime config injected by the Express server into window.__LEDGER_CONFIG__
// Falls back to relative path for local dev (where Vite proxy handles it)
declare global {
  interface Window {
    __LEDGER_CONFIG__?: { apiUrl?: string }
  }
}

/** Raw shape returned by the provisioning endpoints (varies) */
interface RawProvisioningResponse {
  provisioned?: boolean
  state?: string
  steps?: unknown[]
  guidance?: string
  provisioning?: {
    completed?: boolean
    state?: string
    steps?: unknown[]
    description?: string
    explanation?: string
    accepts?: string[]
  }
  current_state?: { state?: string }
}

function resolveApiBase(): string {
  const apiUrl = window.__LEDGER_CONFIG__?.apiUrl || import.meta.env.VITE_LEDGER_API_URL
  if (apiUrl) {
    // Strip trailing slash, append /api/v1
    return `${apiUrl.replace(/\/$/, '')}/api/v1`
  }
  return '/api/v1'
}

function resolveApiBaseV2(): string {
  const apiUrl = window.__LEDGER_CONFIG__?.apiUrl || import.meta.env.VITE_LEDGER_API_URL
  if (apiUrl) {
    return `${apiUrl.replace(/\/$/, '')}/api/v2`
  }
  return '/api/v2'
}

function resolveApiBaseV3(): string {
  const apiUrl = window.__LEDGER_CONFIG__?.apiUrl || import.meta.env.VITE_LEDGER_API_URL
  if (apiUrl) {
    return `${apiUrl.replace(/\/$/, '')}/api/v3`
  }
  return '/api/v3'
}

const BASE = resolveApiBase()
const BASE_V2 = resolveApiBaseV2()
const BASE_V3 = resolveApiBaseV3()
const TOKEN_KEY = 'ledger_core_token'
const ORG_KEY = 'ledger_core_org'

export interface ApiStatus {
  activeReads: number
  activeWrites: number
  avgPingMs: number | null
  lastPingMs: number | null
  connected: boolean
  pendingSync: number
  syncing: boolean
  lastCacheHit: boolean
  /** ISO 8601 timestamp - when the server-side session expires */
  sessionExpiresAt: string | null
  /** Unix ms timestamp - absolute deadline for offline grace (last_seen + 168h) */
  offlineDeadline: number | null
  /** True when the server has rejected the session (re-auth required) */
  sessionDead: boolean
  /** True when in read-only mode (grace expired, user dismissed overlay) */
  readOnly: boolean
  /** True when the WebSocket is disconnected and actively retrying */
  cableReconnecting: boolean
}

export type ApiStatusListener = (status: ApiStatus) => void

const PING_WINDOW = 20

/** Paths that must never be queued for offline sync. */
const NON_QUEUEABLE_PATHS = ['/auth/', '/setup/', '/readiness', '/provisioning/']

/** HTTP methods + path patterns for destructive ops excluded from queueing. */
const DESTRUCTIVE_PATTERNS = ['/void', '/delete', '/dispose', '/deactivate']

class ApiClient {
  private defaultOrgId: string | null = null
  private orgChangeListeners: Array<(orgId: string) => void> = []
  private orgResolvePromise: Promise<string> | null = null

  // --- Status tracking ---
  private _activeReads = 0
  private _activeWrites = 0
  private _pingHistory: number[] = []
  private _connected = true
  private _statusListeners: ApiStatusListener[] = []
  private _lastCacheHit = false
  private _syncing = false
  private _drainPromise: Promise<void> | null = null
  private _wasDisconnected = false
  private _cableUnsub: (() => void) | null = null

  // --- Session / heartbeat ---
  private _sessionExpiresAt: string | null = null
  private _offlineDeadline: number | null = null
  private _sessionDead = false
  private _readOnly = false
  private _heartbeatTimer: ReturnType<typeof setInterval> | null = null
  private _isLeader = false
  private _leaderChannel: BroadcastChannel | null = null
  private _leaderHeartbeatTimer: ReturnType<typeof setInterval> | null = null

  // --- Offline support ---
  readonly offlineStore = new OfflineStore()
  readonly syncQueue = new SyncQueue()
  readonly sequenceManager = new SequenceManager()

  get status(): ApiStatus {
    return {
      activeReads: this._activeReads,
      activeWrites: this._activeWrites,
      avgPingMs: this._pingHistory.length > 0
        ? Math.round(this._pingHistory.reduce((a, b) => a + b, 0) / this._pingHistory.length)
        : null,
      lastPingMs: this._pingHistory.length > 0 ? this._pingHistory[this._pingHistory.length - 1] : null,
      connected: this._connected,
      pendingSync: this.syncQueue.pending,
      syncing: this._syncing,
      lastCacheHit: this._lastCacheHit,
      sessionExpiresAt: this._sessionExpiresAt,
      offlineDeadline: this._offlineDeadline,
      sessionDead: this._sessionDead,
      readOnly: this._readOnly,
      cableReconnecting: isCableReconnecting(),
    }
  }

  onStatusChange(listener: ApiStatusListener) {
    this._statusListeners.push(listener)
    // Forward cable reconnecting state changes to status listeners
    if (this._statusListeners.length === 1) {
      this._cableUnsub = onCableStatusChange((reconnecting) => {
        // Cable finished reconnecting → mark connected
        if (!reconnecting) {
          this._connected = true
          this._wasDisconnected = false
          this._offlineDeadline = null
        }
        this.emitStatus()
      })
    }
  }

  offStatusChange(listener: ApiStatusListener) {
    this._statusListeners = this._statusListeners.filter((l) => l !== listener)
    if (this._statusListeners.length === 0 && this._cableUnsub) {
      this._cableUnsub()
      this._cableUnsub = null
    }
  }

  private emitStatus() {
    const s = this.status
    for (const l of this._statusListeners) l(s)
  }

  recordPing(ms: number) {
    this._pingHistory.push(ms)
    if (this._pingHistory.length > PING_WINDOW) this._pingHistory.shift()
    const wasDisconnected = !this._connected || this._wasDisconnected
    this._connected = true
    this._wasDisconnected = false
    this.emitStatus()

    // On reconnect: refresh token if needed, then drain queue
    if (wasDisconnected && this.syncQueue.pending > 0) {
      void this.onReconnect()
    }
  }

  recordPingFailure() {
    if (this._connected) this._wasDisconnected = true
    this._connected = false

    // Enter offline mode - compute local offline deadline from last known session expiry
    if (this._sessionExpiresAt && !this._offlineDeadline) {
      const expiresMs = new Date(this._sessionExpiresAt).getTime()
      this._offlineDeadline = expiresMs + OFFLINE_GRACE_SECONDS * 1000
    }

    this.emitStatus()
  }

  /** Handle reconnection: try heartbeat first, then refresh, then drain. */
  private async onReconnect(): Promise<void> {
    if (this._drainPromise) return
    this._drainPromise = this.drainReconnect()
    try {
      await this._drainPromise
    } finally {
      this._drainPromise = null
    }
  }

  private async drainReconnect(): Promise<void> {
    // Step 1: Try heartbeat (works if token isn't too old)
    try {
      const hb = await this.heartbeat()
      if (hb.token) {
        this.setToken(hb.token)
        updateCableToken(hb.token)
      }
      this._sessionExpiresAt = hb.session_expires_at
      this._offlineDeadline = null
      this._sessionDead = false
      this._readOnly = false
      this.emitStatus()
    } catch {
      // Step 2: Heartbeat failed - try offline refresh
      try {
        await this.refreshToken()
        this._sessionDead = false
        this._readOnly = false
        this._offlineDeadline = null
        this.emitStatus()
      } catch {
        // Both failed - session is dead, need re-auth
        this._sessionDead = true
        this._readOnly = true
        this.emitStatus()
        return // Don't drain - user must re-auth
      }
    }

    // Step 3: Session restored - drain the queue
    this._syncing = true
    this.emitStatus()

    try {
      await this.syncQueue.drain(
        async (path, options) => {
          return this.request(path, options)
        },
        (_remaining, _total, _current) => {
          this.emitStatus()
        },
      )
    } finally {
      this._syncing = false
      this.emitStatus()
    }
  }

  // --- Heartbeat ---

  async heartbeat(): Promise<{ session_expires_at: string; online: boolean; token?: string; was_offline?: boolean }> {
    const url = `${BASE}/auth/heartbeat`
    const token = this.getToken()
    const headers: Record<string, string> = { 'Content-Type': 'application/json' }
    if (token) headers['Authorization'] = `Bearer ${token}`

    const res = await fetch(url, { method: 'POST', headers })
    if (!res.ok) {
      throw new Error(`Heartbeat failed: ${res.status}`)
    }
    return res.json()
  }

  startHeartbeat() {
    this.stopHeartbeat()
    // Always clear the stale leadership key so this tab can claim
    // immediately. Without this, a page refresh leaves a "fresh" key
    // from the previous page load and the new page waits up to
    // HEARTBEAT_INTERVAL_MS before claiming — meaning no heartbeat fires.
    try { localStorage.removeItem('ledger-heartbeat-leader') } catch { /* SSR-safe */ }
    this.initLeaderElection()

    // Start the 10-second WebSocket latency ping loop
    startWsPingLoop((ms) => this.recordPing(ms))

    // Set initial session expires from the current token
    const token = this.getToken()
    if (token) {
      const parsed = parseToken(token)
      if (parsed) {
        this._sessionExpiresAt = new Date(parsed.exp * 1000).toISOString()
        this.emitStatus()
      }
    }
  }

  stopHeartbeat() {
    if (this._heartbeatTimer) {
      clearInterval(this._heartbeatTimer)
      this._heartbeatTimer = null
    }
    stopWsPingLoop()
    this.destroyLeaderElection()
  }

  private doHeartbeatTick = async () => {
    if (!this._isLeader) return
    try {
      const result = await this.heartbeat()
      if (result.token) {
        this.setToken(result.token)
        updateCableToken(result.token)
      }
      this._sessionExpiresAt = result.session_expires_at
      this._offlineDeadline = null
      this._connected = true
      this._wasDisconnected = false
      this.emitStatus()

      // Broadcast to other tabs
      this._leaderChannel?.postMessage({
        type: 'heartbeat-result',
        sessionExpiresAt: result.session_expires_at,
        token: result.token,
      })
    } catch {
      // Heartbeat failed - likely offline
      this.recordPingFailure()
    }
  }

  // --- Leader Election (BroadcastChannel) ---

  private initLeaderElection() {
    this.destroyLeaderElection()

    try {
      this._leaderChannel = new BroadcastChannel('ledger-heartbeat')
    } catch {
      // BroadcastChannel not supported - this tab is always leader
      this._isLeader = true
      this._heartbeatTimer = setInterval(this.doHeartbeatTick, HEARTBEAT_INTERVAL_MS)
      return
    }

    this._leaderChannel.onmessage = (event) => {
      const data = event.data
      if (data?.type === 'leader-claim') {
        // Another tab claimed leadership - we are a follower
        this._isLeader = false
        if (this._heartbeatTimer) {
          clearInterval(this._heartbeatTimer)
          this._heartbeatTimer = null
        }
      }
      if (data?.type === 'heartbeat-result') {
        // Follower receives heartbeat result from leader
        this._sessionExpiresAt = data.sessionExpiresAt
        if (data.token) this.setToken(data.token)
        this._offlineDeadline = null
        this._connected = true
        this.emitStatus()
      }
    }

    // Try to claim leadership
    this.claimLeadership()

    // Re-claim periodically in case the leader tab closes
    this._leaderHeartbeatTimer = setInterval(() => {
      this.claimLeadership()
    }, HEARTBEAT_INTERVAL_MS / 2)
  }

  private claimLeadership() {
    const key = 'ledger-heartbeat-leader'
    const now = Date.now()
    const existing = localStorage.getItem(key)
    const existingTs = existing ? parseInt(existing, 10) : 0

    // Claim if no leader or leader is stale (hasn't updated in 1 heartbeat interval)
    if (!existing || now - existingTs > HEARTBEAT_INTERVAL_MS) {
      localStorage.setItem(key, String(now))
      this._isLeader = true
      this._leaderChannel?.postMessage({ type: 'leader-claim' })

      if (!this._heartbeatTimer) {
        this._heartbeatTimer = setInterval(this.doHeartbeatTick, HEARTBEAT_INTERVAL_MS)
        // Fire an immediate tick so we don't wait a full interval
        void this.doHeartbeatTick()
      }
    } else if (this._isLeader) {
      // We're the leader - refresh our timestamp
      localStorage.setItem(key, String(now))
    }
  }

  private destroyLeaderElection() {
    if (this._leaderHeartbeatTimer) {
      clearInterval(this._leaderHeartbeatTimer)
      this._leaderHeartbeatTimer = null
    }
    if (this._heartbeatTimer) {
      clearInterval(this._heartbeatTimer)
      this._heartbeatTimer = null
    }
    this._leaderChannel?.close()
    this._leaderChannel = null
    // Clear our leadership claim so a fresh page load can claim immediately
    if (this._isLeader) {
      try { localStorage.removeItem('ledger-heartbeat-leader') } catch { /* SSR-safe */ }
    }
    this._isLeader = false
  }

  // --- Read-only mode ---

  setReadOnly(readOnly: boolean) {
    this._readOnly = readOnly
    this.emitStatus()
  }

  /** Called after successful OAuth re-auth from overlay */
  onReauthenticated(newToken: string) {
    this.setToken(newToken)
    this._sessionDead = false
    this._readOnly = false
    this._offlineDeadline = null

    const parsed = parseToken(newToken)
    if (parsed) {
      this._sessionExpiresAt = new Date(parsed.exp * 1000).toISOString()
    }

    this.emitStatus()
    updateCableToken(newToken)

    // Resume heartbeat and drain queue
    this.startHeartbeat()
    if (this.syncQueue.pending > 0) {
      void this.onReconnect()
    }
  }

  /**
   * Returns the API server origin (e.g. "https://host:3000").
   * Empty string when running in dev with Vite proxy (relative paths).
   */
  get apiOrigin(): string {
    const apiUrl = window.__LEDGER_CONFIG__?.apiUrl || import.meta.env.VITE_LEDGER_API_URL
    return apiUrl ? apiUrl.replace(/\/$/, '') : ''
  }

  private getToken(): string | null {
    return localStorage.getItem(TOKEN_KEY)
  }

  setToken(token: string) {
    localStorage.setItem(TOKEN_KEY, token)
  }

  clearToken() {
    localStorage.removeItem(TOKEN_KEY)
    localStorage.removeItem(ORG_KEY)
    this.defaultOrgId = null
  }

  isAuthenticated(): boolean {
    return !!this.getToken()
  }

  private async request<T>(path: string, options?: RequestInit, apiVersion: 'v1' | 'v2' | 'v3' = 'v1'): Promise<T> {
    // Showcase mode - return fixture data without hitting the network
    if (isShowcaseMode()) {
      const method = (options?.method || 'GET').toUpperCase()
      return showcaseRequest(path, method) as T
    }

    const base = apiVersion === 'v3' ? BASE_V3 : apiVersion === 'v2' ? BASE_V2 : BASE
    const url = `${base}${path}`
    const token = this.getToken()
    const isFormData = options?.body instanceof FormData
    const headers: Record<string, string> = {
      ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
      ...(options?.headers as Record<string, string>),
    }
    if (token) {
      headers['Authorization'] = `Bearer ${token}`
    }

    const method = (options?.method || 'GET').toUpperCase()
    const isWrite = method !== 'GET' && method !== 'HEAD'

    // Enforce read-only mode - reject writes immediately
    if (isWrite && this._readOnly) {
      throw new OfflineError('You must reconnect and sign in before making changes.')
    }

    if (isWrite) { this._activeWrites++; } else { this._activeReads++; }
    this._lastCacheHit = false
    this.emitStatus()

    try {
      // ── Try WebSocket transport first ──────────────────────────
      const wsData = await this.tryWsTransport<T>(path, method, options, apiVersion)
      if (wsData !== undefined) {
        if (!isWrite) { this.offlineStore.setCache(path, wsData) }
        return wsData
      }

      // ── Fallback to HTTP fetch ─────────────────────────────────
      const res = await fetch(url, { ...options, headers })

      // Readiness endpoint returns 503 when not ready - still parse the JSON
      if (!res.ok && res.status !== 503) {
        const data = await res.json().catch(() => ({ message: res.statusText }))
        if (res.status === 401) {
          this.clearToken()
        }
        throw new ApiError(res.status, data)
      }

      const data = (await res.json()) as T

      // Cache successful GET responses
      if (!isWrite) {
        this.offlineStore.setCache(path, data)
      }

      return data
    } catch (error) {
      // Network failure - attempt offline fallback
      if (OfflineError.isNetworkError(error)) {
        this._connected = false
        this._wasDisconnected = true
        this.emitStatus()

        if (!isWrite) {
          // Try returning cached data for reads
          const cached = this.offlineStore.getCache<T>(path)
          if (cached) {
            this._lastCacheHit = true
            this.emitStatus()
            return cached.data
          }
          throw new OfflineError()
        }

        // For writes: queue the mutation if eligible
        if (this.isQueueable(path, method)) {
          this.syncQueue.enqueue({
            method,
            path,
            body: options?.body as string | undefined,
            headers: options?.headers as Record<string, string> | undefined,
            description: `${method} ${path}`,
          })
          this.emitStatus()
          // Return a minimal acknowledgment so the caller doesn't crash
          return { _queued: true, _queuedAt: Date.now() } as unknown as T
        }

        throw new OfflineError(
          'This action requires an active connection. Please try again when online.',
        )
      }

      throw error
    } finally {
      if (isWrite) { this._activeWrites = Math.max(0, this._activeWrites - 1); } else { this._activeReads = Math.max(0, this._activeReads - 1); }
      this.emitStatus()
    }
  }

  // ─── WS paths that must stay on HTTP (binary/streaming) ─────
  private static WS_EXCLUDED = [/\/pdf$/, /\/export/, /\/download/, /\/csv$/, /\/upload/]

  /**
   * Attempt to send the request over the ApiChannel WebSocket.
   * Returns parsed JSON data on success, or `undefined` if WS is
   * unavailable / timed out (caller should fall back to HTTP).
   */
  private async tryWsTransport<T>(
    path: string,
    method: string,
    options?: RequestInit,
    apiVersion: 'v1' | 'v2' | 'v3' = 'v1',
  ): Promise<T | undefined> {
    // Skip WS for binary endpoints
    if (ApiClient.WS_EXCLUDED.some((re) => re.test(path))) return undefined
    // Skip if WS not ready (not connected + authenticated)
    if (!isWsReady()) return undefined

    const requestId = crypto.randomUUID()
    const body = options?.body as string | undefined
    const wsPath = `/api/${apiVersion}${path}`

    try {
      const res = await wsRequest(requestId, method, wsPath, body ?? null)

      if (res.status === 426) {
        // Backend says "use HTTP for this endpoint"
        return undefined
      }

      // Channel-level protocol errors (bad path, missing request_id)
      // should fall back to HTTP, not propagate as API errors.
      if (res.status === 400) {
        const parsed = res.body ? JSON.parse(res.body) : {}
        if (parsed.error === 'bad_request' || parsed.error === 'unauthorized') {
          return undefined
        }
      }

      if (res.status === 401) {
        this.clearToken()
      }

      if (res.status >= 400) {
        const data = res.body ? JSON.parse(res.body) : { message: 'Request failed' }
        throw new ApiError(res.status, data)
      }

      return (res.body ? JSON.parse(res.body) : {}) as T
    } catch (err) {
      // WS transport failures (timeout, disconnect) → fall back to HTTP
      if (err instanceof ApiError) throw err
      return undefined
    }
  }

  /** Check if a mutation is eligible for offline queueing. */
  private isQueueable(path: string, _method: string): boolean {
    // Never queue auth/setup/system requests
    if (NON_QUEUEABLE_PATHS.some((p) => path.includes(p))) return false
    // Never queue destructive operations
    if (DESTRUCTIVE_PATTERNS.some((p) => path.endsWith(p))) return false
    return true
  }

  /**
   * Warm the cache with commonly needed data.
   * Called after successful authentication.
   */
  async warmCache(): Promise<void> {
    const orgId = this.currentOrgId
    if (!orgId) return

    // Fire all requests in parallel - failures are silently ignored
    const warmPaths = [
      `/organisations/${orgId}`,
      `/organisations/${orgId}/accounts`,
      `/organisations/${orgId}/customers`,
      `/organisations/${orgId}/suppliers`,
      '/auth/me',
    ]

    await Promise.allSettled(warmPaths.map((path) => this.request(path)))

    // Also reserve sequence blocks if we don't have any
    if (!this.sequenceManager.hasBlocks()) {
      try {
        const res = await this.request<{ reservations: Record<string, { start: number; count: number; prefix: string }> }>(
          `/organisations/${orgId}/sequence_reservations`,
          {
            method: 'POST',
            body: JSON.stringify({
              client_id: this.sequenceManager.getClientId(),
            }),
          },
        )
        this.sequenceManager.storeBlocks(res.reservations)
      } catch {
        // Non-critical - offline document creation just won't be available
      }
    }
  }

  /**
   * Resolve the active organisation ID.
   *
   * Priority:
   * 1. Explicitly selected org (via switchOrg / localStorage)
   * 2. User's personal org (from /me memberships)
   * 3. First org in the list
   *
   * Deduplicates concurrent calls to avoid parallel API hits.
   */
  private orgId(): Promise<string> {
    if (this.defaultOrgId) return Promise.resolve(this.defaultOrgId)

    // Check localStorage for a previously selected org
    const saved = localStorage.getItem(ORG_KEY)
    if (saved) {
      this.defaultOrgId = saved
      return Promise.resolve(saved)
    }

    if (this.orgResolvePromise) return this.orgResolvePromise

    this.orgResolvePromise = this.resolveOrgId().finally(() => {
      this.orgResolvePromise = null
    })
    return this.orgResolvePromise
  }

  private async resolveOrgId(): Promise<string> {
    // Try to pick the personal org from the user's memberships
    try {
      const me = await this.getMe()
      const personalMembership = me.memberships?.find((m) => m.personal)
      if (personalMembership) {
        this.defaultOrgId = personalMembership.organisation_id
        localStorage.setItem(ORG_KEY, this.defaultOrgId)
        return this.defaultOrgId
      }
      // Fall back to first membership
      if (me.memberships?.length) {
        this.defaultOrgId = me.memberships[0].organisation_id
        localStorage.setItem(ORG_KEY, this.defaultOrgId)
        return this.defaultOrgId
      }
    } catch {
      // /me might fail - fall through to orgs list
    }

    // Last resort: fetch orgs list
    const orgs = await this.getOrganisations()
    const defaultOrg = orgs.find((o) => o.personal) || orgs.find((o) => o.default) || orgs[0]
    if (!defaultOrg) throw new Error('No organisation found. Provision the system first.')
    this.defaultOrgId = defaultOrg.id
    localStorage.setItem(ORG_KEY, this.defaultOrgId)
    return this.defaultOrgId
  }

  /** Switch the active organisation. Clears cached data and notifies listeners. */
  switchOrg(orgId: string) {
    this.defaultOrgId = orgId
    localStorage.setItem(ORG_KEY, orgId)
    this.orgChangeListeners.forEach((fn) => fn(orgId))
  }

  /** Get the currently active org ID (synchronous, may be null if not yet resolved). */
  get currentOrgId(): string | null {
    return this.defaultOrgId || localStorage.getItem(ORG_KEY)
  }

  /** Subscribe to org changes. Returns an unsubscribe function. */
  onOrgChange(fn: (orgId: string) => void): () => void {
    this.orgChangeListeners.push(fn)
    return () => {
      this.orgChangeListeners = this.orgChangeListeners.filter((l) => l !== fn)
    }
  }

  clearOrgCache() {
    this.defaultOrgId = null
  }

  // ─── Readiness ───────────────────────────────────────────

  async getReadiness(): Promise<ReadinessResponse> {
    return this.request<ReadinessResponse>('/readiness')
  }

  async getReadinessChecks(): Promise<ReadinessCheck[]> {
    const data = await this.request<{ checks: ReadinessCheck[] } | ReadinessCheck[]>('/readiness/checks')
    return Array.isArray(data) ? data : data.checks ?? []
  }

  // ─── Provisioning ───────────────────────────────────────

  async getProvisioningStatus(): Promise<ProvisioningStatus> {
    const data = await this.request<RawProvisioningResponse>('/provisioning/status')
    return {
      provisioned: !!(data.provisioned ?? data.provisioning?.completed),
      state: data.current_state?.state ?? data.state,
      steps: data.provisioning?.steps ?? data.steps,
      description: data.provisioning?.description,
      explanation: data.provisioning?.explanation,
      accepts: data.provisioning?.accepts,
      guidance: data.guidance,
    } as ProvisioningStatus
  }

  async initialise(params?: {
    organisation_name?: string
    default_currency?: string
  }): Promise<ProvisioningStatus> {
    this.clearOrgCache()
    const data = await this.request<RawProvisioningResponse>('/provisioning/initialise', {
      method: 'POST',
      body: JSON.stringify(params || {}),
    })
    return {
      provisioned: !!(data.provisioned ?? data.provisioning?.completed),
      state: data.provisioning?.state ?? data.state,
      steps: data.provisioning?.steps ?? data.steps,
      guidance: data.guidance,
    } as ProvisioningStatus
  }

  // ─── Organisations ──────────────────────────────────────

  async getOrganisations(): Promise<Organisation[]> {
    const data = await this.request<{ organisations: Organisation[] } | Organisation[]>('/organisations')
    return Array.isArray(data) ? data : data.organisations ?? []
  }

  async getActiveOrgId(): Promise<string> {
    return this.orgId()
  }

  async getOrganisation(id: string): Promise<Organisation> {
    const data = await this.request<{ organisation: Organisation }>(`/organisations/${id}`)
    return data.organisation
  }

  async updateOrganisation(params: Partial<{ name: string; default_currency: string }>): Promise<Organisation> {
    const oid = await this.orgId()
    const data = await this.request<{ organisation: Organisation }>(`/organisations/${oid}`, {
      method: 'PATCH',
      body: JSON.stringify({ organisation: params }),
    })
    return data.organisation
  }

  async provisionOrganisationAccounts(id: string, default_currency?: string, template?: string): Promise<Organisation> {
    const data = await this.request<{ organisation: Organisation }>(`/organisations/${id}/provision_accounts`, {
      method: 'POST',
      body: JSON.stringify({ default_currency, template }),
    })
    return data.organisation
  }

  async resetOrganisation(id: string): Promise<Organisation> {
    const data = await this.request<{ organisation: Organisation; reset: boolean }>(`/organisations/${id}/reset`, {
      method: 'POST',
    })
    return data.organisation
  }

  async cancelOrganisation(id: string, params: { effective_at?: string; reason?: string } = {}): Promise<{ organisation: Organisation; cancellation: { effective_at?: string; purge_at?: string; lifecycle_state: string } }> {
    return this.request(`/organisations/${id}/cancel`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(params),
    })
  }

  async reactivateOrganisation(id: string): Promise<{ organisation: Organisation; lifecycle_state: string }> {
    return this.request(`/organisations/${id}/reactivate`, { method: 'POST' })
  }

  async getFinancialHealth(id: string): Promise<{ financial_health: Record<string, unknown> | null }> {
    return this.request(`/organisations/${id}/financial_health`)
  }

  async getAccountMappings(id: string): Promise<AccountMappingsResponse> {
    return this.request<AccountMappingsResponse>(`/organisations/${id}/account_mappings`)
  }

  async updateAccountMappings(id: string, mappings: AccountMappings): Promise<AccountMappingsResponse> {
    return this.request<AccountMappingsResponse>(`/organisations/${id}/account_mappings`, {
      method: 'PATCH',
      body: JSON.stringify({ account_mappings: mappings }),
    })
  }

  async adminSuspendOrg(id: string, reason?: string): Promise<{ organisation: unknown; lifecycle_state: string }> {
    return this.request(`/admin/organisations/${id}/suspend`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ reason }),
    })
  }

  async adminUnsuspendOrg(id: string): Promise<{ organisation: unknown; lifecycle_state: string }> {
    return this.request(`/admin/organisations/${id}/unsuspend`, { method: 'POST' })
  }

  async adminDetachOrg(id: string, reason?: string): Promise<{ organisation: unknown; lifecycle_state: string }> {
    return this.request(`/admin/organisations/${id}/detach`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ reason }),
    })
  }

  async adminRestoreOrg(id: string): Promise<{ organisation: unknown; lifecycle_state: string }> {
    return this.request(`/admin/organisations/${id}/restore`, { method: 'POST' })
  }

  // ─── Financial Reports ─────────────────────────────────

  async getProfitLoss(params: { from: string; to: string; currency?: string }): Promise<ProfitLossReport> {
    const oid = await this.orgId()
    const query = new URLSearchParams({ from: params.from, to: params.to })
    if (params.currency) query.set('currency', params.currency)
    return this.request<ProfitLossReport>(`/organisations/${oid}/reports/profit_loss?${query.toString()}`)
  }

  async getProfitLossTrend(params?: { months?: number; end_month?: string; currency?: string }): Promise<ProfitLossTrendReport> {
    const oid = await this.orgId()
    const query = new URLSearchParams()
    if (typeof params?.months === 'number') query.set('months', String(params.months))
    if (params?.end_month) query.set('end_month', params.end_month)
    if (params?.currency) query.set('currency', params.currency)
    const qs = query.toString() ? `?${query.toString()}` : ''
    return this.request<ProfitLossTrendReport>(`/organisations/${oid}/reports/profit_loss_trend${qs}`)
  }

  async getBalanceSheet(params?: { as_of?: string; currency?: string }): Promise<BalanceSheetReport> {
    const oid = await this.orgId()
    const query = new URLSearchParams()
    if (params?.as_of) query.set('as_of', params.as_of)
    if (params?.currency) query.set('currency', params.currency)
    const qs = query.toString() ? `?${query.toString()}` : ''
    return this.request<BalanceSheetReport>(`/organisations/${oid}/reports/balance_sheet${qs}`)
  }

  async getTrialBalance(params?: { as_of?: string; currency?: string }): Promise<TrialBalanceReport> {
    const oid = await this.orgId()
    const query = new URLSearchParams()
    if (params?.as_of) query.set('as_of', params.as_of)
    if (params?.currency) query.set('currency', params.currency)
    const qs = query.toString() ? `?${query.toString()}` : ''
    return this.request<TrialBalanceReport>(`/organisations/${oid}/reports/trial_balance${qs}`)
  }

  async getGstSummary(params: {
    period_from: string
    period_to: string
    basis?: 'accrual' | 'cash'
  }): Promise<GstSummaryReport> {
    const oid = await this.orgId()
    const query = new URLSearchParams({
      period_from: params.period_from,
      period_to: params.period_to,
    })
    if (params.basis) query.set('basis', params.basis)
    return this.request<GstSummaryReport>(`/organisations/${oid}/reports/gst_summary?${query.toString()}`)
  }

  async getGeneralLedger(params: {
    from: string
    to: string
    account_id?: string
    currency?: string
    limit?: number
  }): Promise<GeneralLedgerReport> {
    const oid = await this.orgId()
    const query = new URLSearchParams({ from: params.from, to: params.to })
    if (params.account_id) query.set('account_id', params.account_id)
    if (params.currency) query.set('currency', params.currency)
    if (typeof params.limit === 'number') query.set('limit', String(params.limit))
    return this.request<GeneralLedgerReport>(`/organisations/${oid}/reports/general_ledger?${query.toString()}`)
  }

  async getCashFlowSummary(params: { from: string; to: string; currency?: string }): Promise<CashFlowSummaryReport> {
    const oid = await this.orgId()
    const query = new URLSearchParams({ from: params.from, to: params.to })
    if (params.currency) query.set('currency', params.currency)
    return this.request<CashFlowSummaryReport>(`/organisations/${oid}/reports/cash_flow_summary?${query.toString()}`)
  }

  async getArAging(params?: { as_of?: string; currency?: string }): Promise<ArAgingReport> {
    const oid = await this.orgId()
    const query = new URLSearchParams()
    if (params?.as_of) query.set('as_of', params.as_of)
    if (params?.currency) query.set('currency', params.currency)
    const qs = query.toString() ? `?${query.toString()}` : ''
    return this.request<ArAgingReport>(`/organisations/${oid}/reports/ar_aging${qs}`)
  }

  async getApAging(params?: { as_of?: string; currency?: string }): Promise<ApAgingReport> {
    const oid = await this.orgId()
    const query = new URLSearchParams()
    if (params?.as_of) query.set('as_of', params.as_of)
    if (params?.currency) query.set('currency', params.currency)
    const qs = query.toString() ? `?${query.toString()}` : ''
    return this.request<ApAgingReport>(`/organisations/${oid}/reports/ap_aging${qs}`)
  }

  async getPeriodCloseStatus(): Promise<PeriodCloseStatus> {
    const oid = await this.orgId()
    return this.request<PeriodCloseStatus>(`/organisations/${oid}/period_close`)
  }

  async closePeriod(params: { through: string }): Promise<PeriodCloseResult> {
    const oid = await this.orgId()
    return this.request<PeriodCloseResult>(`/organisations/${oid}/period_close`, {
      method: 'POST',
      body: JSON.stringify(params),
    })
  }

  async setOrganisationCountry(orgId: string, countryCode: string): Promise<Organisation> {
    const data = await this.request<{ organisation: Organisation }>(`/organisations/${orgId}/set_country`, {
      method: 'POST',
      body: JSON.stringify({ country_code: countryCode }),
    })
    return data.organisation
  }

  async reopenPeriod(params?: { through?: string }): Promise<PeriodCloseResult> {
    const oid = await this.orgId()
    return this.request<PeriodCloseResult>(`/organisations/${oid}/period_close/reopen`, {
      method: 'POST',
      body: JSON.stringify(params || {}),
    })
  }

  async createOrganisation(params: {
    name: string
    default_currency?: string
    plan_id?: string
    billing?: {
      billing_contact_name?: string
      billing_contact_email?: string
      billing_address?: string
      country?: string
      tax_id?: string
      currency?: string
      stripe_secret_key?: string
      stripe_webhook_secret?: string
    }
  }): Promise<Organisation> {
    const data = await this.request<{ organisation: Organisation }>('/organisations', {
      method: 'POST',
      body: JSON.stringify({
        organisation: { name: params.name },
        default_currency: params.default_currency,
        plan_id: params.plan_id,
        billing: params.billing,
      }),
    })
    return data.organisation
  }

  // ─── Subscriptions ──────────────────────────────────────

  async getSubscriptions(): Promise<Subscription[]> {
    const data = await this.request<{ subscriptions: Subscription[] } | Subscription[]>('/subscriptions')
    return Array.isArray(data) ? data : data.subscriptions ?? []
  }

  async createSubscription(planId: string): Promise<Subscription> {
    const data = await this.request<{ subscription: Subscription }>('/subscriptions', {
      method: 'POST',
      body: JSON.stringify({ plan_id: planId }),
    })
    return data.subscription
  }

  async cancelSubscription(id: string): Promise<Subscription> {
    const data = await this.request<{ subscription: Subscription }>(`/subscriptions/${id}/cancel`, {
      method: 'POST',
    })
    return data.subscription
  }

  // ─── Accounts (auto-resolves default org) ───────────────

  async getAccounts(params?: { include_system?: boolean }): Promise<Account[]> {
    const oid = await this.orgId()
    const query = new URLSearchParams()
    if (params?.include_system) query.set('include_system', 'true')
    const qs = query.toString() ? `?${query.toString()}` : ''
    const data = await this.request<{ accounts: Account[] } | Account[]>(
      `/organisations/${oid}/accounts${qs}`,
    )
    return Array.isArray(data) ? data : data.accounts ?? []
  }

  async createAccount(account: {
    name: string
    code: string
    account_type: string
    currency?: string
    allow_negative?: boolean
  }): Promise<Account> {
    const oid = await this.orgId()
    const data = await this.request<{ account: Account } | Account>(
      `/organisations/${oid}/accounts`,
      {
        method: 'POST',
        body: JSON.stringify({
          account: {
            ...account,
            balance_policy: account.allow_negative ? 'unrestricted' : 'restricted',
          },
        }),
      },
    )
    return 'account' in data ? data.account : data
  }

  async getAccountBalance(accountId: string): Promise<AccountBalance> {
    const oid = await this.orgId()
    return this.request<AccountBalance>(`/organisations/${oid}/accounts/${accountId}/balance`)
  }

  async updateAccount(accountId: string, data: {
    name?: string
    description?: string
    currency?: string
    balance_policy?: string
    active?: boolean
  }): Promise<Account> {
    const oid = await this.orgId()
    const res = await this.request<{ account: Account } | Account>(
      `/organisations/${oid}/accounts/${accountId}`,
      { method: 'PATCH', body: JSON.stringify({ account: data }) },
    )
    return 'account' in res ? res.account : res
  }

  async deleteAccount(accountId: string): Promise<void> {
    const oid = await this.orgId()
    await this.request(`/organisations/${oid}/accounts/${accountId}`, { method: 'DELETE' })
  }

  async deleteEmptyAccounts(template?: string): Promise<{ deleted: number }> {
    const oid = await this.orgId()
    const url = template
      ? `/organisations/${oid}/accounts/delete_empty?template=${encodeURIComponent(template)}`
      : `/organisations/${oid}/accounts/delete_empty`
    return this.request(url, { method: 'DELETE' })
  }

  // ─── Journal Entries (auto-resolves default org) ────────

  async getJournalEntries(): Promise<JournalEntry[]> {
    const oid = await this.orgId()
    const data = await this.request<{ journal_entries: JournalEntry[] } | JournalEntry[]>(
      `/organisations/${oid}/journal_entries`,
    )
    return Array.isArray(data) ? data : data.journal_entries ?? []
  }

  async createJournalEntry(entry: {
    description: string
    entry_lines_attributes: {
      account_id: string
      direction: 'debit' | 'credit'
      amount: string
      currency: string
    }[]
  }): Promise<JournalEntry> {
    const oid = await this.orgId()
    const data = await this.request<{ journal_entry: JournalEntry } | JournalEntry>(
      `/organisations/${oid}/journal_entries`,
      {
        method: 'POST',
        body: JSON.stringify({ journal_entry: entry }),
      },
    )
    return 'journal_entry' in data ? data.journal_entry : data
  }

  async postJournalEntry(id: string): Promise<JournalEntry> {
    const oid = await this.orgId()
    const data = await this.request<{ journal_entry: JournalEntry } | JournalEntry>(
      `/organisations/${oid}/journal_entries/${id}/post`,
      { method: 'POST' },
    )
    return 'journal_entry' in data ? data.journal_entry : data
  }

  async voidJournalEntry(id: string, reason: string): Promise<JournalEntry> {
    const oid = await this.orgId()
    const data = await this.request<{ journal_entry: JournalEntry } | JournalEntry>(
      `/organisations/${oid}/journal_entries/${id}/void`,
      {
        method: 'POST',
        body: JSON.stringify({ reason }),
      },
    )
    return 'journal_entry' in data ? data.journal_entry : data
  }

  async importBankStatement(params: {
    format: BankStatementFormat
    data: string
    bank_account_id: string
    offset_account_id: string
    default_currency?: string
    post_immediately?: boolean
    qif_date_format?: QifDateFormat
    dry_run?: boolean
  }): Promise<BankStatementImportResult> {
    const oid = await this.orgId()
    const data = await this.request<{ import: BankStatementImportResult }>(
      `/organisations/${oid}/journal_entries/import_statement`,
      {
        method: 'POST',
        body: JSON.stringify(params),
      },
    )
    return data.import
  }

  // ─── Auth ───────────────────────────────────────────────

  async getSetupStatus(): Promise<SetupStatus> {
    return this.request<SetupStatus>('/auth/setup-status')
  }

  async getAuthProviders(): Promise<{ providers: Array<{ name: string; authorize_url: string; display_name?: string }>; needs_setup: boolean }> {
    return this.request('/auth/providers')
  }

  // ─── Device Auth (desktop) ─────────────────────────────

  async deviceAuthStart(deviceName: string, platform: string): Promise<{
    user_code: string
    device_auth_id: string
    expires_in: number
    interval: number
  }> {
    return this.request('/device-auth/start', {
      method: 'POST',
      body: JSON.stringify({ device_name: deviceName, platform }),
    })
  }

  async deviceAuthExchange(deviceAuthId: string): Promise<{
    status: 'authorization_pending' | 'access_denied' | 'approved'
    device_token?: string
    access_token?: string
    device_id?: string
  }> {
    return this.request('/device-auth/exchange', {
      method: 'POST',
      body: JSON.stringify({ device_auth_id: deviceAuthId }),
    })
  }

  async deviceAuthToken(deviceToken: string): Promise<{ access_token: string }> {
    return this.request('/device-auth/token', {
      method: 'POST',
      body: JSON.stringify({ device_token: deviceToken }),
    })
  }

  async deviceAuthRevoke(deviceToken: string): Promise<void> {
    await this.request('/device-auth/revoke', {
      method: 'POST',
      body: JSON.stringify({ device_token: deviceToken }),
    })
  }

  // OAuth login is handled by browser redirect to /auth/:provider
  // The callback page captures the token from the URL query params

  async getMe(): Promise<User> {
    const data = await this.request<User | { user: User }>('/auth/me')
    // The API returns the user object directly (not wrapped in { user: ... })
    const user = 'user' in data ? data.user : data as User
    // Derive membership_role from memberships array for the current org
    if (user.memberships?.length && !user.membership_role) {
      const currentOrgId = this.defaultOrgId || localStorage.getItem('ledger_org_id')
      const membership = currentOrgId
        ? user.memberships.find((m) => m.organisation_id === currentOrgId)
        : user.memberships[0]
      if (membership) user.membership_role = membership.role
    }
    return user
  }

  async refreshToken(): Promise<string> {
    const data = await this.request<{ token: string; session_expires_at?: string }>('/auth/refresh', { method: 'POST' })
    this.setToken(data.token)
    if (data.session_expires_at) {
      this._sessionExpiresAt = data.session_expires_at
      this.emitStatus()
    }
    // Send fresh token to the existing WebSocket (no reconnect needed)
    updateCableToken(data.token)
    return data.token
  }

  async serverLogout(): Promise<void> {
    try {
      await this.request('/auth/logout', { method: 'DELETE' })
    } catch { /* best-effort - token may already be expired */ }
  }

  logout() {
    this.stopHeartbeat()
    disconnectCable()
    this.clearToken()
  }

  async completeOnboarding(termsAccepted?: boolean): Promise<{ onboarded: boolean }> {
    return this.request('/auth/complete_onboarding', {
      method: 'POST',
      body: JSON.stringify({ terms_accepted: termsAccepted ?? true }),
    })
  }

  async updatePreferences(prefs: {
    ui_mode?: string
    classic_mode_source?: string
    guided_transition_accepted_at?: string
    last_transition_nudged_at?: string
    guided_transition_declined_count?: number
    classic_welcome_dismissed_at?: string
  }): Promise<User> {
    const data = await this.request<{ user: User } | User>('/auth/preferences', {
      method: 'PATCH',
      body: JSON.stringify(prefs),
    })
    return 'user' in data ? data.user : data as User
  }

  async logTransitionEvent(actionName: string, details: Record<string, unknown> = {}): Promise<void> {
    await this.request('/profile/transition_events', {
      method: 'POST',
      body: JSON.stringify({ action_name: actionName, details }),
    })
  }

  // ─── Invitation (public) ────────────────────────────────

  async getInvitationByToken(token: string): Promise<InvitationPreview> {
    return this.request<InvitationPreview>(`/invitations/${token}`)
  }

  async acceptInvitation(token: string): Promise<{ message: string }> {
    return this.request(`/invitations/${token}/accept`, { method: 'POST' })
  }

  async declineInvitation(token: string): Promise<{ message: string }> {
    return this.request(`/invitations/${token}/decline`, { method: 'POST' })
  }

  // ─── Users (org-scoped) ─────────────────────────────────

  async getUsers(): Promise<User[]> {
    const oid = await this.orgId()
    const data = await this.request<{ users: User[] } | User[]>(`/organisations/${oid}/users`)
    return Array.isArray(data) ? data : data.users ?? []
  }

  async updateUser(userId: string, params: { name?: string; role?: string }): Promise<User> {
    const oid = await this.orgId()
    const data = await this.request<{ user: User }>(`/organisations/${oid}/users/${userId}`, {
      method: 'PATCH',
      body: JSON.stringify({ user: params }),
    })
    return data.user
  }

  async deactivateUser(userId: string): Promise<User> {
    const oid = await this.orgId()
    const data = await this.request<{ user: User }>(`/organisations/${oid}/users/${userId}/deactivate`, {
      method: 'POST',
    })
    return data.user
  }

  async reactivateUser(userId: string): Promise<User> {
    const oid = await this.orgId()
    const data = await this.request<{ user: User }>(`/organisations/${oid}/users/${userId}/reactivate`, {
      method: 'POST',
    })
    return data.user
  }

  async removeUserFromOrg(userId: string): Promise<{ message: string }> {
    const oid = await this.orgId()
    return this.request(`/organisations/${oid}/users/${userId}/membership`, {
      method: 'DELETE',
    })
  }

  // ─── Invitations (org-scoped) ───────────────────────────

  async getInvitations(status?: string): Promise<Invitation[]> {
    const oid = await this.orgId()
    const qs = status ? `?status=${status}` : ''
    const data = await this.request<{ invitations: Invitation[] } | Invitation[]>(
      `/organisations/${oid}/invitations${qs}`,
    )
    return Array.isArray(data) ? data : data.invitations ?? []
  }

  async createInvitation(params: { email: string; role?: string }): Promise<{ invitation: Invitation; accept_url: string }> {
    const oid = await this.orgId()
    return this.request(`/organisations/${oid}/invitations`, {
      method: 'POST',
      body: JSON.stringify(params),
    })
  }

  async revokeInvitation(invitationId: string): Promise<Invitation> {
    const oid = await this.orgId()
    const data = await this.request<{ invitation: Invitation }>(
      `/organisations/${oid}/invitations/${invitationId}/revoke`,
      { method: 'POST' },
    )
    return data.invitation
  }

  // ─── Usage Metering ─────────────────────────────────────

  async getUsageSummary(params?: { from?: string; to?: string }): Promise<UsageSummaryResponse> {
    const oid = await this.orgId()
    const query = new URLSearchParams()
    if (params?.from) query.set('from', params.from)
    if (params?.to) query.set('to', params.to)
    const qs = query.toString() ? `?${query.toString()}` : ''
    return this.request<UsageSummaryResponse>(`/organisations/${oid}/usage${qs}`)
  }

  async getUsageDailyTotals(days?: number): Promise<UsageDailyTotalsResponse> {
    const oid = await this.orgId()
    const qs = days ? `?days=${days}` : ''
    return this.request<UsageDailyTotalsResponse>(`/organisations/${oid}/usage/daily_totals${qs}`)
  }

  async getUsageCurrent(): Promise<UsageCurrentResponse> {
    const oid = await this.orgId()
    return this.request<UsageCurrentResponse>(`/organisations/${oid}/usage/current`)
  }

  // ─── Audit Logs ─────────────────────────────────────────

  async getAuditLogs(params?: {
    actor_type?: string
    action?: string
    resource_type?: string
    from?: string
    to?: string
    page?: number
    per_page?: number
  }): Promise<AuditLogsResponse> {
    const oid = await this.orgId()
    const query = new URLSearchParams()
    if (params?.actor_type) query.set('actor_type', params.actor_type)
    if (params?.action) query.set('action', params.action)
    if (params?.resource_type) query.set('resource_type', params.resource_type)
    if (params?.from) query.set('from', params.from)
    if (params?.to) query.set('to', params.to)
    if (params?.page) query.set('page', String(params.page))
    if (params?.per_page) query.set('per_page', String(params.per_page))
    const qs = query.toString() ? `?${query.toString()}` : ''
    return this.request<AuditLogsResponse>(`/organisations/${oid}/audit_logs${qs}`)
  }

  async getAuditLog(id: string): Promise<AuditLogEntry> {
    const oid = await this.orgId()
    return this.request<AuditLogEntry>(`/organisations/${oid}/audit_logs/${id}`)
  }

  // ─── System Info ────────────────────────────────────────

  async getSystemInfo(): Promise<SystemInfoResponse> {
    return this.request<SystemInfoResponse>('/system_info')
  }

  // ─── API Tokens (org-scoped) ────────────────────────────

  async getApiTokens(): Promise<ApiToken[]> {
    const oid = await this.orgId()
    const data = await this.request<{ api_tokens: ApiToken[] } | ApiToken[]>(
      `/organisations/${oid}/api_tokens`,
    )
    return Array.isArray(data) ? data : data.api_tokens ?? []
  }

  async createApiToken(params: {
    name: string
    scopes: string[]
    expires_in_days?: number
  }): Promise<ApiTokenCreateResponse> {
    const oid = await this.orgId()
    return this.request<ApiTokenCreateResponse>(`/organisations/${oid}/api_tokens`, {
      method: 'POST',
      body: JSON.stringify(params),
    })
  }

  async revokeApiToken(tokenId: string): Promise<void> {
    const oid = await this.orgId()
    await this.request<void>(`/organisations/${oid}/api_tokens/${tokenId}`, {
      method: 'DELETE',
    })
  }

  async getApiTokenScopes(): Promise<{ scopes: Record<string, string> }> {
    const oid = await this.orgId()
    return this.request(`/organisations/${oid}/api_tokens/scopes`)
  }

  // ─── Email Settings (admin) ─────────────────────────────

  async getEmailSettings(): Promise<EmailSettings> {
    return this.request<EmailSettings>('/settings/email')
  }

  // --- Admin: platform-wide user management ---

  async adminListUsers(): Promise<{ users: AdminUser[] }> {
    return this.request<{ users: AdminUser[] }>('/admin/users')
  }

  async adminUpdateUser(userId: string, role: UserRole): Promise<{ user: AdminUser }> {
    return this.request<{ user: AdminUser }>(`/admin/users/${userId}`, {
      method: 'PATCH',
      body: JSON.stringify({ role }),
    })
  }

  async adminDeactivateUser(userId: string): Promise<{ user: AdminUser; message: string }> {
    return this.request<{ user: AdminUser; message: string }>(`/admin/users/${userId}/deactivate`, {
      method: 'POST',
    })
  }

  async adminReactivateUser(userId: string): Promise<{ user: AdminUser; message: string }> {
    return this.request<{ user: AdminUser; message: string }>(`/admin/users/${userId}/reactivate`, {
      method: 'POST',
    })
  }

  async adminDeleteUser(userId: string): Promise<{ message: string }> {
    return this.request<{ message: string }>(`/admin/users/${userId}`, { method: 'DELETE' })
  }

  async adminDeleteOrganisation(orgId: string): Promise<{ message: string }> {
    return this.request<{ message: string }>(`/admin/organisations/${orgId}`, { method: 'DELETE' })
  }

  async adminBackfillPersonalOrgs(): Promise<{ provisioned: number; message: string }> {
    return this.request<{ provisioned: number; message: string }>('/admin/backfill_personal_orgs', { method: 'POST' })
  }

  async adminListOrganisations(): Promise<{ organisations: AdminOrganisation[] }> {
    return this.request<{ organisations: AdminOrganisation[] }>('/admin/organisations')
  }

  async adminUpdateBusinessFeaturesOverride(id: string, enabled: boolean): Promise<AdminOrganisation> {
    return this.request<AdminOrganisation>(`/admin/organisations/${id}/business_features_override`, {
      method: 'PATCH',
      body: JSON.stringify({ enabled }),
    })
  }

  async adminCreateOrganisation(params: {
    name: string
    slug?: string
    default_currency?: string
    plan_id?: string
    owner_id?: string
    billing?: {
      billing_contact_name?: string
      billing_contact_email?: string
      country?: string
      currency?: string
    }
  }): Promise<{ organisation: AdminOrganisation; message: string }> {
    return this.request<{ organisation: AdminOrganisation; message: string }>('/admin/organisations', {
      method: 'POST',
      body: JSON.stringify(params),
    })
  }

  async adminTransferOwnership(orgId: string, userId: string): Promise<{ organisation: AdminOrganisation; message: string }> {
    return this.request<{ organisation: AdminOrganisation; message: string }>(`/admin/organisations/${orgId}/transfer_ownership`, {
      method: 'POST',
      body: JSON.stringify({ user_id: userId }),
    })
  }

  async getAnnouncementSettings(): Promise<AnnouncementSettings> {
    return this.request<AnnouncementSettings>('/settings/announcement')
  }

  async updateAnnouncementSettings(settings: {
    enabled?: boolean
    message?: string
    level?: AnnouncementLevel
    dismissible?: boolean
    pay_attention_style?: AnnouncementPayAttentionStyle
  }): Promise<{ updated: boolean; message: string; announcement: AnnouncementSettings }> {
    return this.request<{ updated: boolean; message: string; announcement: AnnouncementSettings }>('/settings/announcement', {
      method: 'PUT',
      body: JSON.stringify(settings),
    })
  }

  async updateEmailSettings(settings: {
    postmark_api_key?: string
    from_address?: string
    from_name?: string
  }): Promise<EmailSettings> {
    return this.request<EmailSettings>('/settings/email', {
      method: 'PUT',
      body: JSON.stringify(settings),
    })
  }

  async testEmail(to: string): Promise<{ message: string }> {
    return this.request<{ message: string }>('/settings/email/test', {
      method: 'POST',
      body: JSON.stringify({ to }),
    })
  }

  // ─── Stripe Settings (admin) ────────────────────────────

  async getStripeSettings(): Promise<StripeSettings> {
    return this.request<StripeSettings>('/settings/stripe')
  }

  async updateStripeSettings(settings: {
    publishable_key?: string
    secret_key?: string
    webhook_secret?: string
  }): Promise<{ updated: boolean; message: string }> {
    return this.request<{ updated: boolean; message: string }>('/settings/stripe', {
      method: 'PUT',
      body: JSON.stringify(settings),
    })
  }

  // ─── System Settings (admin) ────────────────────────────

  async getSystemSettings(category?: string): Promise<{ settings: SystemSettingEntry[]; categories: string[] }> {
    const qs = category ? `?category=${encodeURIComponent(category)}` : ''
    return this.request<{ settings: SystemSettingEntry[]; categories: string[] }>(`/admin/system_settings${qs}`)
  }

  async updateSystemSetting(params: {
    key: string
    value: string
    category?: string
    encrypt?: boolean
  }): Promise<{ setting: SystemSettingEntry; message: string }> {
    return this.request<{ setting: SystemSettingEntry; message: string }>('/admin/system_settings', {
      method: 'PUT',
      body: JSON.stringify(params),
    })
  }

  async deleteSystemSetting(key: string): Promise<{ message: string }> {
    return this.request<{ message: string }>(`/admin/system_settings/${encodeURIComponent(key)}`, {
      method: 'DELETE',
    })
  }

  // ─── Notification Settings (admin) ──────────────────────

  async getNotificationSettings(): Promise<NotificationSettings> {
    const oid = await this.orgId()
    const data = await this.request<{ notification_preferences: NotificationSettings['preferences'] }>(
      `/organisations/${oid}/notification_preferences`,
    )
    return { preferences: Array.isArray(data) ? data : data.notification_preferences ?? [] }
  }

  async updateNotificationPreference(
    prefId: string,
    params: { enabled?: boolean; channels?: string[] },
  ): Promise<void> {
    const oid = await this.orgId()
    await this.request(`/organisations/${oid}/notification_preferences/${prefId}`, {
      method: 'PATCH',
      body: JSON.stringify({ notification_preference: params }),
    })
  }

  // ─── Plans (owner-only) ─────────────────────────────────

  async getPlans(): Promise<Plan[]> {
    const data = await this.request<{ plans: Plan[] } | Plan[]>('/plans')
    return Array.isArray(data) ? data : data.plans ?? []
  }

  async getPlan(id: string): Promise<Plan> {
    return this.request<Plan>(`/plans/${id}`)
  }

  async createPlan(plan: {
    name: string
    slug?: string
    rate_limit_a?: number | null
    rate_limit_b?: number | null
    rate_limit_c?: number | null
    rate_limit_d?: number | null
    monthly_quota_a?: number | null
    monthly_quota_b?: number | null
    monthly_quota_c?: number | null
    monthly_quota_d?: number | null
    base_fee_cents?: number
    base_fee_currency?: string
    overage_rate_a_cents?: number
    overage_rate_b_cents?: number
    overage_rate_c_cents?: number
    overage_rate_d_cents?: number
    overage_currency?: string
    fx_margin_percent?: number
    features?: Record<string, unknown>
  }): Promise<Plan> {
    return this.request<Plan>('/plans', {
      method: 'POST',
      body: JSON.stringify(plan),
    })
  }

  async updatePlan(id: string, plan: Partial<{
    name: string
    rate_limit_a: number | null
    rate_limit_b: number | null
    rate_limit_c: number | null
    rate_limit_d: number | null
    monthly_quota_a: number | null
    monthly_quota_b: number | null
    monthly_quota_c: number | null
    monthly_quota_d: number | null
    base_fee_cents: number
    base_fee_currency: string
    overage_rate_a_cents: number
    overage_rate_b_cents: number
    overage_rate_c_cents: number
    overage_rate_d_cents: number
    overage_currency: string
    fx_margin_percent: number
    features: Record<string, unknown>
  }>): Promise<Plan> {
    return this.request<Plan>(`/plans/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(plan),
    })
  }

  async deletePlan(id: string): Promise<void> {
    await this.request<void>(`/plans/${id}`, { method: 'DELETE' })
  }

  async assignPlan(planId: string, organisationId: string): Promise<{ message: string }> {
    return this.request(`/plans/${planId}/assign`, {
      method: 'POST',
      body: JSON.stringify({ organisation_id: organisationId }),
    })
  }

  // ─── Rate Limits ────────────────────────────────────────

  async getRateLimitStatus(): Promise<RateLimitStatusResponse> {
    const oid = await this.orgId()
    return this.request<RateLimitStatusResponse>(`/organisations/${oid}/rate_limits`)
  }

  // ─── Billing Profile ────────────────────────────────────

  async getBillingProfile(): Promise<BillingProfile> {
    const oid = await this.orgId()
    return this.request<BillingProfile>(`/organisations/${oid}/billing_profile`)
  }

  async updateBillingProfile(params: Partial<{
    billing_name: string
    billing_email: string
    address_line_1: string
    address_line_2: string
    city: string
    region: string
    postal_code: string
    country: string
    tax_id: string
    tax_rate: number
    charity: boolean
    charity_verified: boolean
    currency: string
    payment_terms_days: number
    grace_period_days: number
    billing_anchor_day: number
    invoice_prefix: string
    stripe_secret_key: string
    stripe_webhook_secret: string
    reminder_schedule: number[]
    restrict_after_days: number
    payment_methods: Record<string, unknown>
    surcharge_label: string
    surcharge_taxable: boolean
    auto_apply_credit_notes: boolean
    default_amounts_tax_inclusive: boolean
  }>): Promise<BillingProfile> {
    const oid = await this.orgId()
    return this.request<BillingProfile>(`/organisations/${oid}/billing_profile`, {
      method: 'PATCH',
      body: JSON.stringify(params),
    })
  }

  // ─── Invoices ───────────────────────────────────────────

  async getInvoices(params?: { status?: string; type?: string; page?: number; per_page?: number }): Promise<InvoicesResponse> {
    const oid = await this.orgId()
    const query = new URLSearchParams()
    if (params?.status) query.set('status', params.status)
    if (params?.type) query.set('type', params.type)
    if (params?.page) query.set('page', String(params.page))
    if (params?.per_page) query.set('per_page', String(params.per_page))
    const qs = query.toString() ? `?${query.toString()}` : ''
    return this.request<InvoicesResponse>(`/organisations/${oid}/invoices${qs}`)
  }

  async getInvoice(id: string): Promise<InvoiceDetail> {
    const oid = await this.orgId()
    return this.request<InvoiceDetail>(`/organisations/${oid}/invoices/${id}`)
  }

  async createInvoice(data: Record<string, unknown>): Promise<InvoiceDetail> {
    const oid = await this.orgId()
    return this.request<InvoiceDetail>(`/organisations/${oid}/invoices`, {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  async generateInvoice(params?: { period_start?: string; period_end?: string }): Promise<InvoiceDetail> {
    const oid = await this.orgId()
    return this.request<InvoiceDetail>(`/organisations/${oid}/invoices/generate`, {
      method: 'POST',
      body: JSON.stringify(params || {}),
    })
  }

  async updateInvoice(id: string, data: Record<string, unknown>): Promise<InvoiceDetail> {
    const oid = await this.orgId()
    return this.request<InvoiceDetail>(`/organisations/${oid}/invoices/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    })
  }

  async issueInvoice(id: string): Promise<InvoiceDetail> {
    const oid = await this.orgId()
    return this.request<InvoiceDetail>(`/organisations/${oid}/invoices/${id}/issue`, {
      method: 'POST',
    })
  }

  async sendInvoice(id: string): Promise<InvoiceDetail> {
    const oid = await this.orgId()
    return this.request<InvoiceDetail>(`/organisations/${oid}/invoices/${id}/send`, {
      method: 'POST',
    })
  }

  async recordPayment(id: string, stripePaymentIntentId?: string): Promise<InvoiceDetail> {
    const oid = await this.orgId()
    return this.request<InvoiceDetail>(`/organisations/${oid}/invoices/${id}/record_payment`, {
      method: 'POST',
      body: JSON.stringify({ stripe_payment_intent_id: stripePaymentIntentId }),
    })
  }

  async voidInvoice(id: string, reason: string): Promise<InvoiceDetail> {
    const oid = await this.orgId()
    return this.request<InvoiceDetail>(`/organisations/${oid}/invoices/${id}/void`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    })
  }

  async deleteInvoice(id: string): Promise<void> {
    const oid = await this.orgId()
    return this.request<void>(`/organisations/${oid}/invoices/${id}`, {
      method: 'DELETE',
    })
  }

  async revertInvoiceToDraft(id: string): Promise<InvoiceDetail> {
    const oid = await this.orgId()
    return this.request<InvoiceDetail>(`/organisations/${oid}/invoices/${id}/revert_to_draft`, {
      method: 'POST',
    })
  }

  private async downloadPdf(path: string, filename: string): Promise<void> {
    const token = this.getToken()
    const headers: Record<string, string> = {}
    if (token) headers['Authorization'] = `Bearer ${token}`
    const res = await fetch(`${BASE}${path}`, { headers })
    if (!res.ok) throw new ApiError(res.status, { message: 'PDF download failed' })
    const blob = await res.blob()
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = filename
    document.body.appendChild(a)
    a.click()
    a.remove()
    URL.revokeObjectURL(url)
  }

  async downloadInvoicePdf(id: string): Promise<void> {
    const oid = await this.orgId()
    return this.downloadPdf(`/organisations/${oid}/invoices/${id}/pdf`, `invoice-${id}.pdf`)
  }

  async regenerateInvoiceShareLink(id: string): Promise<{ share_token: string; share_url: string }> {
    const oid = await this.orgId()
    return this.request(`/organisations/${oid}/invoices/${id}/regenerate_share_link`, { method: 'POST' })
  }

  async sendInvoiceReminder(id: string): Promise<{ sent: boolean }> {
    const oid = await this.orgId()
    return this.request(`/organisations/${oid}/invoices/${id}/send_reminder`, { method: 'POST' })
  }

  // ─── Customers ──────────────────────────────────────────

  async getCustomers(search?: string): Promise<Customer[]> {
    const oid = await this.orgId()
    const qs = search ? `?search=${encodeURIComponent(search)}` : ''
    const data = await this.request<{ customers: Customer[] }>(`/organisations/${oid}/customers${qs}`)
    return data.customers
  }

  async getCustomer(id: string): Promise<CustomerDetail> {
    const oid = await this.orgId()
    return this.request<CustomerDetail>(`/organisations/${oid}/customers/${id}`)
  }

  async createCustomer(params: {
    name: string
    email: string
    phone?: string
    tax_id?: string
    address_line_1?: string
    address_line_2?: string
    city?: string
    region?: string
    postal_code?: string
    country?: string
    metadata?: Record<string, unknown>
  }): Promise<Customer> {
    const oid = await this.orgId()
    return this.request<Customer>(`/organisations/${oid}/customers`, {
      method: 'POST',
      body: JSON.stringify(params),
    })
  }

  async updateCustomer(id: string, params: Partial<{
    name: string
    email: string
    phone: string
    tax_id: string
    address_line_1: string
    address_line_2: string
    city: string
    region: string
    postal_code: string
    country: string
    active: boolean
  }>): Promise<Customer> {
    const oid = await this.orgId()
    return this.request<Customer>(`/organisations/${oid}/customers/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(params),
    })
  }

  async deleteCustomer(id: string): Promise<void> {
    const oid = await this.orgId()
    await this.request<void>(`/organisations/${oid}/customers/${id}`, { method: 'DELETE' })
  }

  async sendPortalLink(customerId: string): Promise<{ message: string; portal_active: boolean }> {
    const oid = await this.orgId()
    return this.request<{ message: string; portal_active: boolean }>(`/organisations/${oid}/customers/${customerId}/send_portal_link`, {
      method: 'POST',
    })
  }

  // ─── Suppliers ──────────────────────────────────────────

  async getSuppliers(search?: string): Promise<Supplier[]> {
    const oid = await this.orgId()
    const qs = search ? `?search=${encodeURIComponent(search)}` : ''
    const data = await this.request<{ suppliers: Supplier[] }>(`/organisations/${oid}/suppliers${qs}`)
    return data.suppliers
  }

  async getSupplier(id: string): Promise<SupplierDetail> {
    const oid = await this.orgId()
    return this.request<SupplierDetail>(`/organisations/${oid}/suppliers/${id}`)
  }

  async createSupplier(params: {
    name: string
    email?: string
    phone?: string
    tax_id?: string
    currency?: string
    address_line_1?: string
    address_line_2?: string
    city?: string
    region?: string
    postal_code?: string
    country?: string
  }): Promise<Supplier> {
    const oid = await this.orgId()
    return this.request<Supplier>(`/organisations/${oid}/suppliers`, {
      method: 'POST',
      body: JSON.stringify(params),
    })
  }

  async updateSupplier(id: string, params: Partial<{
    name: string
    email: string
    phone: string
    tax_id: string
    currency: string
    address_line_1: string
    city: string
    country: string
    active: boolean
  }>): Promise<Supplier> {
    const oid = await this.orgId()
    return this.request<Supplier>(`/organisations/${oid}/suppliers/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(params),
    })
  }

  async deleteSupplier(id: string): Promise<void> {
    const oid = await this.orgId()
    await this.request<void>(`/organisations/${oid}/suppliers/${id}`, { method: 'DELETE' })
  }

  // ─── Bills (Accounts Payable) ──────────────────────────

  async getBills(params?: { status?: string; supplier_id?: string; per_page?: number }): Promise<Bill[]> {
    const oid = await this.orgId()
    const query = new URLSearchParams()
    if (params?.status) query.set('status', params.status)
    if (params?.supplier_id) query.set('supplier_id', params.supplier_id)
    query.set('per_page', String(params?.per_page ?? 100))
    const qs = query.toString() ? `?${query.toString()}` : ''
    const data = await this.request<{ bills: Bill[] }>(`/organisations/${oid}/bills${qs}`)
    return data.bills
  }

  async getBill(id: string): Promise<BillDetail> {
    const oid = await this.orgId()
    return this.request<BillDetail>(`/organisations/${oid}/bills/${id}`)
  }

  async createBill(params: {
    supplier_id: string
    bill_number?: string
    bill_date?: string
    due_at?: string
    notes?: string
    currency?: string
    line_items?: Array<{
      account_id?: string
      description: string
      quantity?: number
      unit_price_cents?: number
      currency?: string
      tax_code?: string
      tax_rate?: number
    }>
  }): Promise<BillDetail> {
    const oid = await this.orgId()
    return this.request<BillDetail>(`/organisations/${oid}/bills`, {
      method: 'POST',
      body: JSON.stringify(params),
    })
  }

  async updateBill(id: string, params: Partial<{
    supplier_id: string | null
    bill_number: string
    bill_date: string
    due_at: string
    notes: string
    currency: string
    amounts_tax_inclusive: boolean
    line_items: Array<{
      account_id?: string
      description: string
      quantity?: number
      unit_price_cents?: number
      currency?: string
      tax_code?: string
      tax_rate?: number
    }>
  }>): Promise<BillDetail> {
    const oid = await this.orgId()
    return this.request<BillDetail>(`/organisations/${oid}/bills/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(params),
    })
  }

  async issueBill(id: string): Promise<BillDetail> {
    const oid = await this.orgId()
    return this.request<BillDetail>(`/organisations/${oid}/bills/${id}/issue`, {
      method: 'POST',
    })
  }

  async payBill(id: string, paymentAccountId: string): Promise<BillDetail> {
    const oid = await this.orgId()
    return this.request<BillDetail>(`/organisations/${oid}/bills/${id}/record_payment`, {
      method: 'POST',
      body: JSON.stringify({ payment_account_id: paymentAccountId }),
    })
  }

  async payBillSplit(id: string, splits: { account_id: string; amount_cents: number }[]): Promise<BillDetail> {
    const oid = await this.orgId()
    return this.request<BillDetail>(`/organisations/${oid}/bills/${id}/record_payment`, {
      method: 'POST',
      body: JSON.stringify({ payment_splits: splits }),
    })
  }

  async bulkPayBills(billIds: string[], paymentAccountId: string): Promise<{ paid: BillDetail[] }> {
    const oid = await this.orgId()
    return this.request<{ paid: BillDetail[] }>(`/organisations/${oid}/bills/bulk_pay`, {
      method: 'POST',
      body: JSON.stringify({ bill_ids: billIds, payment_account_id: paymentAccountId }),
    })
  }

  async voidBill(id: string, reason?: string): Promise<BillDetail> {
    const oid = await this.orgId()
    return this.request<BillDetail>(`/organisations/${oid}/bills/${id}/void`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    })
  }

  async deleteBill(id: string): Promise<void> {
    const oid = await this.orgId()
    return this.request<void>(`/organisations/${oid}/bills/${id}`, {
      method: 'DELETE',
    })
  }

  async revertBillToDraft(id: string): Promise<BillDetail> {
    const oid = await this.orgId()
    return this.request<BillDetail>(`/organisations/${oid}/bills/${id}/revert_to_draft`, {
      method: 'POST',
    })
  }


  // ─── Inventory / Recipes / Sales ──────────────────────

  async getInventoryItems(): Promise<InventoryItem[]> {
    const oid = await this.orgId()
    const data = await this.request<{ items: InventoryItem[] }>(`/organisations/${oid}/inventory/items`)
    return data.items
  }

  async createInventoryItem(params: {
    name: string
    sku?: string
    base_unit?: string
    pack_unit?: string
    pack_to_base_quantity?: number
    reorder_point_quantity?: number
    active?: boolean
    average_unit_cost_cents?: number
    currency?: string
  }): Promise<InventoryItem> {
    const oid = await this.orgId()
    const data = await this.request<{ item: InventoryItem }>(`/organisations/${oid}/inventory/items`, {
      method: 'POST',
      body: JSON.stringify(params),
    })
    return data.item
  }

  async updateInventoryItem(id: string, params: Partial<{
    name: string
    sku: string
    base_unit: string
    pack_unit: string
    pack_to_base_quantity: number
    reorder_point_quantity: number
    active: boolean
    average_unit_cost_cents: number
    currency: string
  }>): Promise<InventoryItem> {
    const oid = await this.orgId()
    const data = await this.request<{ item: InventoryItem }>(`/organisations/${oid}/inventory/items/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(params),
    })
    return data.item
  }

  async getInventoryMovements(): Promise<InventoryMovement[]> {
    const oid = await this.orgId()
    const data = await this.request<{ movements: InventoryMovement[] }>(`/organisations/${oid}/inventory/movements`)
    return data.movements
  }

  async createInventoryMovement(params: {
    inventory_item_id: string
    supplier_id?: string
    bill_id?: string
    movement_type: 'receipt' | 'consumption' | 'adjustment'
    quantity: number
    unit_cost_cents?: number
    occurred_on?: string
    reference_type?: string
    reference_id?: string
    reference?: string
    notes?: string
  }): Promise<InventoryMovement> {
    const oid = await this.orgId()
    const data = await this.request<{ movement: InventoryMovement }>(`/organisations/${oid}/inventory/movements`, {
      method: 'POST',
      body: JSON.stringify(params),
    })
    return data.movement
  }

  async getRecipes(): Promise<Recipe[]> {
    const oid = await this.orgId()
    const data = await this.request<{ recipes: Recipe[] }>(`/organisations/${oid}/inventory/recipes`)
    return data.recipes
  }

  async createRecipe(params: {
    name: string
    code?: string
    yield_quantity?: number
    yield_unit?: string
    active?: boolean
    notes?: string
    lines?: Array<{
      inventory_item_id: string
      quantity: number
      unit?: string
      waste_percent?: number
      sort_order?: number
    }>
  }): Promise<Recipe> {
    const oid = await this.orgId()
    const data = await this.request<{ recipe: Recipe }>(`/organisations/${oid}/inventory/recipes`, {
      method: 'POST',
      body: JSON.stringify(params),
    })
    return data.recipe
  }

  async updateRecipe(id: string, params: Partial<{
    name: string
    code: string
    yield_quantity: number
    yield_unit: string
    active: boolean
    notes: string
    lines: Array<{
      inventory_item_id: string
      quantity: number
      unit?: string
      waste_percent?: number
      sort_order?: number
    }>
  }>): Promise<Recipe> {
    const oid = await this.orgId()
    const data = await this.request<{ recipe: Recipe }>(`/organisations/${oid}/inventory/recipes/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(params),
    })
    return data.recipe
  }

  async getMenuItems(): Promise<MenuItem[]> {
    const oid = await this.orgId()
    const data = await this.request<{ menu_items: MenuItem[] }>(`/organisations/${oid}/inventory/menu_items`)
    return data.menu_items
  }

  async createMenuItem(params: {
    name: string
    sku?: string
    sale_price_cents: number
    currency?: string
    tax_code?: string
    tax_rate?: number
    active?: boolean
    build_rule_id?: string
  }): Promise<MenuItem> {
    const oid = await this.orgId()
    const data = await this.request<{ menu_item: MenuItem }>(`/organisations/${oid}/inventory/menu_items`, {
      method: 'POST',
      body: JSON.stringify(params),
    })
    return data.menu_item
  }

  async updateMenuItem(id: string, params: Partial<{
    name: string
    sku: string
    sale_price_cents: number
    currency: string
    tax_code: string
    tax_rate: number
    active: boolean
    build_rule_id: string
  }>): Promise<MenuItem> {
    const oid = await this.orgId()
    const data = await this.request<{ menu_item: MenuItem }>(`/organisations/${oid}/inventory/menu_items/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(params),
    })
    return data.menu_item
  }

  async getSaleEvents(): Promise<SaleEvent[]> {
    const oid = await this.orgId()
    const data = await this.request<{ sale_events: SaleEvent[] }>(`/organisations/${oid}/inventory/sale_events`)
    return data.sale_events
  }

  async createSaleEvent(params: {
    occurred_at?: string
    notes?: string
    lines: Array<{
      sellable_item_id: string
      quantity: number
      unit_price_cents: number
      tax_rate?: number
    }>
  }): Promise<SaleEvent> {
    const oid = await this.orgId()
    const data = await this.request<{ sale_event: SaleEvent }>(`/organisations/${oid}/inventory/sale_events`, {
      method: 'POST',
      body: JSON.stringify(params),
    })
    return data.sale_event
  }

  // ─── Bank Reconciliation ───────────────────────────────

  async getBankReconciliations(params?: { account_id?: string; status?: string }): Promise<BankReconciliation[]> {
    const oid = await this.orgId()
    const query = new URLSearchParams()
    if (params?.account_id) query.set('account_id', params.account_id)
    if (params?.status) query.set('status', params.status)
    const qs = query.toString() ? `?${query.toString()}` : ''
    const data = await this.request<{ reconciliations: BankReconciliation[] }>(`/organisations/${oid}/bank_reconciliations${qs}`)
    return data.reconciliations
  }

  async createBankReconciliation(params: {
    account_id: string
    period_start: string
    period_end: string
    statement_ending_balance: string | number
    currency?: string
    notes?: string
  }): Promise<BankReconciliationDetail> {
    const oid = await this.orgId()
    return this.request<BankReconciliationDetail>(`/organisations/${oid}/bank_reconciliations`, {
      method: 'POST',
      body: JSON.stringify(params),
    })
  }

  async reconcileBankReconciliation(id: string, auto_adjust = false): Promise<BankReconciliationDetail> {
    const oid = await this.orgId()
    return this.request<BankReconciliationDetail>(`/organisations/${oid}/bank_reconciliations/${id}/reconcile`, {
      method: 'POST',
      body: JSON.stringify({ auto_adjust }),
    })
  }

  // ─── Multi-currency Revaluation ───────────────────────

  async runRevaluation(params?: { as_of?: string; base_currency?: string }): Promise<RevaluationRunResponse> {
    const oid = await this.orgId()
    return this.request<RevaluationRunResponse>(`/organisations/${oid}/revaluations/run`, {
      method: 'POST',
      body: JSON.stringify(params || {}),
    })
  }

  // ─── Invoice Templates ──────────────────────────────────

  async getInvoiceTemplates(): Promise<InvoiceTemplate[]> {
    const oid = await this.orgId()
    const data = await this.request<{ invoice_templates: InvoiceTemplate[] }>(`/organisations/${oid}/invoice_templates`)
    return data.invoice_templates
  }

  async getInvoiceTemplate(id: string): Promise<InvoiceTemplate> {
    const oid = await this.orgId()
    return this.request<InvoiceTemplate>(`/organisations/${oid}/invoice_templates/${id}`)
  }

  async createInvoiceTemplate(params: {
    name: string
    customer_id: string
    schedule: string
    currency: string
    notes?: string
    next_generate_at?: string
    repeat_end_date?: string
    send_on_generate?: boolean
    line_items?: Array<{
      type?: string
      line_type?: string
      description?: string
      quantity?: number
      unit_price_cents?: number
      tax_code?: string
      tax_rate?: number
    }>
    ledger_rules?: Array<Record<string, unknown>>
  }): Promise<InvoiceTemplate> {
    const oid = await this.orgId()
    return this.request<InvoiceTemplate>(`/organisations/${oid}/invoice_templates`, {
      method: 'POST',
      body: JSON.stringify(params),
    })
  }

  async updateInvoiceTemplate(
    id: string,
    params: Partial<{
      name: string
      schedule: string
      currency: string
      notes: string
      active: boolean
      next_generate_at: string
      repeat_end_date: string
      send_on_generate: boolean
      line_items: Array<{
        type?: string
        line_type?: string
        description?: string
        quantity?: number
        unit_price_cents?: number
        tax_code?: string
        tax_rate?: number
      }>
      ledger_rules: Array<Record<string, unknown>>
    }>
  ): Promise<InvoiceTemplate> {
    const oid = await this.orgId()
    return this.request<InvoiceTemplate>(`/organisations/${oid}/invoice_templates/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(params),
    })
  }

  async deleteInvoiceTemplate(id: string): Promise<void> {
    const oid = await this.orgId()
    await this.request<void>(`/organisations/${oid}/invoice_templates/${id}`, { method: 'DELETE' })
  }

  async generateFromTemplate(templateId: string): Promise<{ invoice_id: string; invoice_number: string }> {
    const oid = await this.orgId()
    return this.request(`/organisations/${oid}/invoice_templates/${templateId}/generate`, {
      method: 'POST',
    })
  }

  // ─── SLA Credits ────────────────────────────────────────

  async getSlaCredits(params?: { status?: string; type?: string }): Promise<SlaCreditsResponse> {
    const oid = await this.orgId()
    const query = new URLSearchParams()
    if (params?.status) query.set('status', params.status)
    if (params?.type) query.set('type', params.type)
    const qs = query.toString() ? `?${query.toString()}` : ''
    return this.request<SlaCreditsResponse>(`/organisations/${oid}/sla_credits${qs}`)
  }

  async createSlaCredit(params: {
    credit_type: string
    reason: string
    amount_cents?: number
    operation_class?: string
    usage_amount?: number
    expires_at?: string
  }): Promise<SlaCredit> {
    const oid = await this.orgId()
    return this.request<SlaCredit>(`/organisations/${oid}/sla_credits`, {
      method: 'POST',
      body: JSON.stringify(params),
    })
  }

  async cancelSlaCredit(id: string): Promise<SlaCredit> {
    const oid = await this.orgId()
    return this.request<SlaCredit>(`/organisations/${oid}/sla_credits/${id}/cancel`, {
      method: 'POST',
    })
  }

  // ─── Branding ───────────────────────────────────────────

  async getBranding(): Promise<BrandingAsset> {
    const oid = await this.orgId()
    return this.request<BrandingAsset>(`/organisations/${oid}/branding`)
  }

  async updateBranding(params: Partial<{
    company_name: string
    accent_colour: string
    invoice_footer: string
    email_from_name: string
    email_from_address: string
    custom_domain: string
  }>): Promise<BrandingAsset> {
    const oid = await this.orgId()
    return this.request<BrandingAsset>(`/organisations/${oid}/branding`, {
      method: 'PATCH',
      body: JSON.stringify(params),
    })
  }

  // ─── Content Reviews ───────────────────────────────────

  async getContentReviews(status?: string): Promise<ContentReview[]> {
    const oid = await this.orgId()
    const qs = status ? `?status=${status}` : ''
    const data = await this.request<{ content_reviews: ContentReview[] }>(`/organisations/${oid}/content_reviews${qs}`)
    return data.content_reviews
  }

  async getPendingContentReviews(): Promise<ContentReview[]> {
    const data = await this.request<{ content_reviews: ContentReview[] }>('/content_reviews/pending')
    return data.content_reviews
  }

  async approveContentReview(id: string): Promise<ContentReview> {
    return this.request<ContentReview>(`/content_reviews/${id}/approve`, { method: 'POST' })
  }

  async rejectContentReview(id: string, reason: string): Promise<ContentReview> {
    return this.request<ContentReview>(`/content_reviews/${id}/reject`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    })
  }

  // ─── Exchange Rates ─────────────────────────────────────

  async getCurrentExchangeRate(base?: string, target?: string): Promise<ExchangeRate> {
    const query = new URLSearchParams()
    if (base) query.set('base', base)
    if (target) query.set('target', target)
    const qs = query.toString() ? `?${query.toString()}` : ''
    return this.request<ExchangeRate>(`/exchange_rates/current${qs}`)
  }

  async getExchangeRateHistory(days?: number): Promise<{ rates: ExchangeRate[] }> {
    const qs = days ? `?days=${days}` : ''
    return this.request<{ rates: ExchangeRate[] }>(`/exchange_rates/history${qs}`)
  }

  async fetchExchangeRate(): Promise<ExchangeRate> {
    return this.request<ExchangeRate>('/exchange_rates/fetch', { method: 'POST' })
  }

  // ─── Wallet ─────────────────────────────────────────────

  async getWallet(): Promise<WalletInfo> {
    return this.request<WalletInfo>('/wallet')
  }

  async getWalletTransactions(params?: { page?: number; per_page?: number }): Promise<WalletTransactionsResponse> {
    const query = new URLSearchParams()
    if (params?.page) query.set('page', String(params.page))
    if (params?.per_page) query.set('per_page', String(params.per_page))
    const qs = query.toString() ? `?${query.toString()}` : ''
    return this.request<WalletTransactionsResponse>(`/wallet/transactions${qs}`)
  }

  async topupWallet(amount_cents: number): Promise<{ transaction: WalletTransaction; new_balance_cents: number }> {
    return this.request('/wallet/topup', {
      method: 'POST',
      body: JSON.stringify({ amount_cents }),
    })
  }

  async transferWallet(organisation_id: string, amount_cents: number): Promise<{ transaction: WalletTransaction; new_balance_cents: number }> {
    return this.request('/wallet/transfer', {
      method: 'POST',
      body: JSON.stringify({ organisation_id, amount_cents }),
    })
  }

  // ─── Cards ──────────────────────────────────────────────

  async getCardStatus(): Promise<CardStatus> {
    return this.request<CardStatus>('/cards')
  }

  async createSetupIntent(): Promise<SetupIntentResponse> {
    return this.request<SetupIntentResponse>('/cards/setup_intent', { method: 'POST' })
  }

  async attachCard(payment_method_id: string): Promise<{ card: CardInfo; has_card: boolean; card_verified: boolean }> {
    return this.request('/cards/attach', {
      method: 'POST',
      body: JSON.stringify({ payment_method_id }),
    })
  }

  async detachCard(): Promise<{ has_card: boolean; card_verified: boolean }> {
    return this.request('/cards', { method: 'DELETE' })
  }

  async initiateCardVerification(): Promise<CardVerificationResponse> {
    return this.request<CardVerificationResponse>('/cards/verify', { method: 'POST' })
  }

  async confirmCardVerification(code_1: string, code_2: string): Promise<CardVerifyConfirmResponse> {
    return this.request<CardVerifyConfirmResponse>('/cards/verify/confirm', {
      method: 'POST',
      body: JSON.stringify({ code_1, code_2 }),
    })
  }

  // ─── Quotes ──────────────────────────────────────────────

  async getQuotes(params?: { status?: string }): Promise<{ quotes: Quote[] }> {
    const oid = await this.orgId()
    const query = new URLSearchParams()
    if (params?.status) query.set('status', params.status)
    const qs = query.toString() ? `?${query.toString()}` : ''
    return this.request(`/organisations/${oid}/quotes${qs}`)
  }

  async getQuote(id: string): Promise<Quote> {
    const oid = await this.orgId()
    return this.request(`/organisations/${oid}/quotes/${id}`)
  }

  async createQuote(data: Record<string, unknown>): Promise<{ quote: Quote }> {
    const oid = await this.orgId()
    return this.request(`/organisations/${oid}/quotes`, { method: 'POST', body: JSON.stringify(data) })
  }

  async updateQuote(id: string, data: Record<string, unknown>): Promise<{ quote: Quote }> {
    const oid = await this.orgId()
    return this.request(`/organisations/${oid}/quotes/${id}`, { method: 'PATCH', body: JSON.stringify(data) })
  }

  async sendQuote(id: string): Promise<{ quote: Quote }> {
    const oid = await this.orgId()
    return this.request(`/organisations/${oid}/quotes/${id}/send_quote`, { method: 'POST' })
  }

  async issueQuote(id: string): Promise<{ quote: Quote }> {
    const oid = await this.orgId()
    return this.request(`/organisations/${oid}/quotes/${id}/issue`, { method: 'POST' })
  }

  async acceptQuote(id: string): Promise<{ quote: Quote }> {
    const oid = await this.orgId()
    return this.request(`/organisations/${oid}/quotes/${id}/accept`, { method: 'POST' })
  }

  async declineQuote(id: string): Promise<{ quote: Quote }> {
    const oid = await this.orgId()
    return this.request(`/organisations/${oid}/quotes/${id}/decline`, { method: 'POST' })
  }

  async convertQuoteToInvoice(id: string): Promise<{ invoice_id: string }> {
    const oid = await this.orgId()
    return this.request(`/organisations/${oid}/quotes/${id}/convert`, { method: 'POST' })
  }

  async regenerateQuoteShareLink(id: string): Promise<{ share_token: string; share_url: string }> {
    const oid = await this.orgId()
    return this.request(`/organisations/${oid}/quotes/${id}/regenerate_share_link`, { method: 'POST' })
  }

  async markQuoteWorkComplete(id: string): Promise<Record<string, unknown>> {
    const oid = await this.orgId()
    return this.request(`/organisations/${oid}/quotes/${id}/work_complete`, { method: 'POST' })
  }

  async voidQuote(id: string, reason?: string): Promise<Record<string, unknown>> {
    const oid = await this.orgId()
    return this.request(`/organisations/${oid}/quotes/${id}/void`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    })
  }

  async revertQuoteToDraft(id: string): Promise<Record<string, unknown>> {
    const oid = await this.orgId()
    return this.request(`/organisations/${oid}/quotes/${id}/revert_to_draft`, { method: 'POST' })
  }

  async deleteQuote(id: string): Promise<void> {
    const oid = await this.orgId()
    return this.request<void>(`/organisations/${oid}/quotes/${id}`, {
      method: 'DELETE',
    })
  }

  // ─── Purchase Orders ─────────────────────────────────────

  async getPurchaseOrders(params?: { status?: string }): Promise<{ purchase_orders: PurchaseOrder[] }> {
    const oid = await this.orgId()
    const query = new URLSearchParams()
    if (params?.status) query.set('status', params.status)
    const qs = query.toString() ? `?${query.toString()}` : ''
    return this.request(`/organisations/${oid}/purchase_orders${qs}`)
  }

  async getPurchaseOrder(id: string): Promise<PurchaseOrder> {
    const oid = await this.orgId()
    return this.request(`/organisations/${oid}/purchase_orders/${id}`)
  }

  async createPurchaseOrder(data: Record<string, unknown>): Promise<{ purchase_order: PurchaseOrder }> {
    const oid = await this.orgId()
    return this.request(`/organisations/${oid}/purchase_orders`, { method: 'POST', body: JSON.stringify(data) })
  }

  async updatePurchaseOrder(id: string, data: Record<string, unknown>): Promise<{ purchase_order: PurchaseOrder }> {
    const oid = await this.orgId()
    return this.request(`/organisations/${oid}/purchase_orders/${id}`, { method: 'PATCH', body: JSON.stringify(data) })
  }

  async sendPurchaseOrder(id: string): Promise<{ purchase_order: PurchaseOrder }> {
    const oid = await this.orgId()
    return this.request(`/organisations/${oid}/purchase_orders/${id}/send_po`, { method: 'POST' })
  }

  async issuePurchaseOrder(id: string): Promise<{ purchase_order: PurchaseOrder }> {
    const oid = await this.orgId()
    return this.request(`/organisations/${oid}/purchase_orders/${id}/issue`, { method: 'POST' })
  }

  async revertPurchaseOrderToDraft(id: string): Promise<{ purchase_order: PurchaseOrder }> {
    const oid = await this.orgId()
    return this.request(`/organisations/${oid}/purchase_orders/${id}/revert_to_draft`, { method: 'POST' })
  }

  async regeneratePoShareLink(id: string): Promise<{ share_token: string; share_url: string }> {
    const oid = await this.orgId()
    return this.request(`/organisations/${oid}/purchase_orders/${id}/regenerate_share_link`, { method: 'POST' })
  }

  async receivePurchaseOrder(id: string): Promise<{ purchase_order: PurchaseOrder }> {
    const oid = await this.orgId()
    return this.request(`/organisations/${oid}/purchase_orders/${id}/mark_received`, { method: 'POST' })
  }

  async cancelPurchaseOrder(id: string): Promise<{ purchase_order: PurchaseOrder }> {
    const oid = await this.orgId()
    return this.request(`/organisations/${oid}/purchase_orders/${id}/cancel`, { method: 'POST' })
  }

  async deletePurchaseOrder(id: string): Promise<void> {
    const oid = await this.orgId()
    return this.request<void>(`/organisations/${oid}/purchase_orders/${id}`, {
      method: 'DELETE',
    })
  }

  async downloadPurchaseOrderPdf(id: string): Promise<void> {
    const oid = await this.orgId()
    return this.downloadPdf(`/organisations/${oid}/purchase_orders/${id}/pdf`, `po-${id}.pdf`)
  }

  async downloadCreditNotePdf(id: string): Promise<void> {
    const oid = await this.orgId()
    return this.downloadPdf(`/organisations/${oid}/credit_notes/${id}/pdf`, `credit-note-${id}.pdf`)
  }

  async deleteCreditNote(id: string): Promise<void> {
    const oid = await this.orgId()
    return this.request<void>(`/organisations/${oid}/credit_notes/${id}`, {
      method: 'DELETE',
    })
  }

  async downloadQuotePdf(id: string): Promise<void> {
    const oid = await this.orgId()
    return this.downloadPdf(`/organisations/${oid}/quotes/${id}/pdf`, `quote-${id}.pdf`)
  }

  async convertPurchaseOrderToBill(id: string): Promise<{ bill_id: string }> {
    const oid = await this.orgId()
    return this.request(`/organisations/${oid}/purchase_orders/${id}/convert`, { method: 'POST' })
  }

  // ─── Credit Notes ────────────────────────────────────────

  async getCreditNote(id: string): Promise<CreditNote> {
    const oid = await this.orgId()
    return this.request(`/organisations/${oid}/credit_notes/${id}`)
  }

  async getCreditNotes(params?: { status?: string; type?: string }): Promise<{ credit_notes: CreditNote[] }> {
    const oid = await this.orgId()
    const query = new URLSearchParams()
    if (params?.status) query.set('status', params.status)
    if (params?.type) query.set('credit_note_type', params.type)
    const qs = query.toString() ? `?${query.toString()}` : ''
    return this.request(`/organisations/${oid}/credit_notes${qs}`)
  }

  async createCreditNote(data: Record<string, unknown>): Promise<{ credit_note: CreditNote }> {
    const oid = await this.orgId()
    return this.request(`/organisations/${oid}/credit_notes`, { method: 'POST', body: JSON.stringify(data) })
  }

  async issueCreditNote(id: string): Promise<{ credit_note: CreditNote }> {
    const oid = await this.orgId()
    return this.request(`/organisations/${oid}/credit_notes/${id}/issue`, { method: 'POST' })
  }

  async revertCreditNoteToDraft(id: string): Promise<{ credit_note: CreditNote }> {
    const oid = await this.orgId()
    return this.request(`/organisations/${oid}/credit_notes/${id}/revert_to_draft`, { method: 'POST' })
  }

  async allocateCreditNote(id: string, data: Record<string, unknown>): Promise<unknown> {
    const oid = await this.orgId()
    return this.request(`/organisations/${oid}/credit_notes/${id}/allocate`, { method: 'POST', body: JSON.stringify(data) })
  }

  async regenerateCreditNoteShareLink(id: string): Promise<{ share_token: string; share_url: string }> {
    const oid = await this.orgId()
    return this.request(`/organisations/${oid}/credit_notes/${id}/regenerate_share_link`, { method: 'POST' })
  }

  // ─── Shared Documents (public, no auth) ──────────────────

  async getSharedInvoice(token: string): Promise<Record<string, unknown>> {
    return this.request(`/shared/invoices/${token}`)
  }

  async getSharedQuote(token: string): Promise<Record<string, unknown>> {
    return this.request(`/shared/quotes/${token}`)
  }

  async getSharedCreditNote(token: string): Promise<Record<string, unknown>> {
    return this.request(`/shared/credit_notes/${token}`)
  }

  async acceptSharedQuote(token: string): Promise<Record<string, unknown>> {
    return this.request(`/shared/quotes/${token}/accept`, { method: 'POST' })
  }

  async declineSharedQuote(token: string): Promise<Record<string, unknown>> {
    return this.request(`/shared/quotes/${token}/decline`, { method: 'POST' })
  }

  async createSharedQuotePayment(token: string): Promise<{ client_secret: string; publishable_key: string; payment_intent_id: string }> {
    return this.request(`/shared/quotes/${token}/create_payment`, { method: 'POST' })
  }

  async createSharedInvoicePayment(token: string): Promise<{ client_secret: string; publishable_key: string; payment_intent_id: string }> {
    return this.request(`/shared/invoices/${token}/create_payment`, { method: 'POST' })
  }

  // ─── Expense Claims ──────────────────────────────────────

  async getExpenseClaims(params?: { status?: string }): Promise<{ expense_claims: ExpenseClaim[]; pagination: { page: number; per_page: number; total: number; total_pages: number } }> {
    const oid = await this.orgId()
    const query = new URLSearchParams()
    if (params?.status) query.set('status', params.status)
    const qs = query.toString() ? `?${query.toString()}` : ''
    return this.request(`/organisations/${oid}/expense_claims${qs}`)
  }

  async getExpenseClaim(id: string): Promise<ExpenseClaimDetail> {
    const oid = await this.orgId()
    return this.request(`/organisations/${oid}/expense_claims/${id}`)
  }

  async createExpenseClaim(data: Record<string, unknown>): Promise<ExpenseClaimDetail> {
    const oid = await this.orgId()
    return this.request(`/organisations/${oid}/expense_claims`, { method: 'POST', body: JSON.stringify(data) })
  }

  async updateExpenseClaim(id: string, data: Record<string, unknown>): Promise<ExpenseClaimDetail> {
    const oid = await this.orgId()
    return this.request(`/organisations/${oid}/expense_claims/${id}`, { method: 'PATCH', body: JSON.stringify(data) })
  }

  async submitExpenseClaim(id: string): Promise<ExpenseClaimDetail> {
    const oid = await this.orgId()
    return this.request(`/organisations/${oid}/expense_claims/${id}/submit`, { method: 'POST' })
  }

  async approveExpenseClaim(id: string): Promise<ExpenseClaimDetail> {
    const oid = await this.orgId()
    return this.request(`/organisations/${oid}/expense_claims/${id}/approve`, { method: 'POST' })
  }

  async rejectExpenseClaim(id: string, reason?: string): Promise<ExpenseClaimDetail> {
    const oid = await this.orgId()
    return this.request(`/organisations/${oid}/expense_claims/${id}/reject`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    })
  }

  async reimburseExpenseClaim(id: string): Promise<ExpenseClaimDetail> {
    const oid = await this.orgId()
    return this.request(`/organisations/${oid}/expense_claims/${id}/reimburse`, { method: 'POST' })
  }

  async voidExpenseClaim(id: string): Promise<ExpenseClaimDetail> {
    const oid = await this.orgId()
    return this.request(`/organisations/${oid}/expense_claims/${id}/void`, { method: 'POST' })
  }

  // ─── Tracking Categories ─────────────────────────────────

  async getTrackingCategories(): Promise<{ tracking_categories: TrackingCategory[] }> {
    const oid = await this.orgId()
    return this.request(`/organisations/${oid}/tracking_categories`)
  }

  async createTrackingCategory(data: Record<string, unknown>): Promise<{ tracking_category: TrackingCategory }> {
    const oid = await this.orgId()
    return this.request(`/organisations/${oid}/tracking_categories`, { method: 'POST', body: JSON.stringify(data) })
  }

  async updateTrackingCategory(id: string, data: Record<string, unknown>): Promise<{ tracking_category: TrackingCategory }> {
    const oid = await this.orgId()
    return this.request(`/organisations/${oid}/tracking_categories/${id}`, { method: 'PATCH', body: JSON.stringify(data) })
  }

  // ─── Budgets ─────────────────────────────────────────────

  async getBudgets(): Promise<{ budgets: BudgetData[] }> {
    const oid = await this.orgId()
    return this.request(`/organisations/${oid}/budgets`)
  }

  async getBudget(id: string): Promise<{ budget: BudgetData }> {
    const oid = await this.orgId()
    return this.request(`/organisations/${oid}/budgets/${id}`)
  }

  async createBudget(data: Record<string, unknown>): Promise<{ budget: BudgetData }> {
    const oid = await this.orgId()
    return this.request(`/organisations/${oid}/budgets`, { method: 'POST', body: JSON.stringify(data) })
  }

  // ─── Jobs / Projects ─────────────────────────────────────

  async getJobs(params?: { status?: string }): Promise<{ jobs: JobData[] }> {
    const oid = await this.orgId()
    const query = new URLSearchParams()
    if (params?.status) query.set('status', params.status)
    const qs = query.toString() ? `?${query.toString()}` : ''
    return this.request(`/organisations/${oid}/jobs${qs}`)
  }

  async getJob(id: string): Promise<{ job: JobData }> {
    const oid = await this.orgId()
    return this.request(`/organisations/${oid}/jobs/${id}`)
  }

  async createJob(data: Record<string, unknown>): Promise<{ job: JobData }> {
    const oid = await this.orgId()
    return this.request(`/organisations/${oid}/jobs`, { method: 'POST', body: JSON.stringify(data) })
  }

  async closeJob(id: string): Promise<{ job: JobData }> {
    const oid = await this.orgId()
    return this.request(`/organisations/${oid}/jobs/${id}/close`, { method: 'POST' })
  }

  async reopenJob(id: string): Promise<{ job: JobData }> {
    const oid = await this.orgId()
    return this.request(`/organisations/${oid}/jobs/${id}/reopen`, { method: 'POST' })
  }

  // ─── Fixed Assets ────────────────────────────────────────

  async getFixedAssets(params?: { status?: string }): Promise<{ fixed_assets: FixedAssetData[] }> {
    const oid = await this.orgId()
    const query = new URLSearchParams()
    if (params?.status) query.set('status', params.status)
    const qs = query.toString() ? `?${query.toString()}` : ''
    return this.request(`/organisations/${oid}/fixed_assets${qs}`)
  }

  async createFixedAsset(data: Record<string, unknown>): Promise<{ fixed_asset: FixedAssetData }> {
    const oid = await this.orgId()
    return this.request(`/organisations/${oid}/fixed_assets`, { method: 'POST', body: JSON.stringify(data) })
  }

  async runDepreciation(data: { period: string }): Promise<unknown> {
    const oid = await this.orgId()
    return this.request(`/organisations/${oid}/fixed_assets/depreciate`, { method: 'POST', body: JSON.stringify(data) })
  }

  async disposeFixedAsset(id: string, data: Record<string, unknown>): Promise<unknown> {
    const oid = await this.orgId()
    return this.request(`/organisations/${oid}/fixed_assets/${id}/dispose`, { method: 'POST', body: JSON.stringify(data) })
  }

  // ─── Bank Rules ──────────────────────────────────────────

  async getBankRules(): Promise<{ bank_rules: BankRuleData[] }> {
    const oid = await this.orgId()
    return this.request(`/organisations/${oid}/bank_rules`)
  }

  async createBankRule(data: Record<string, unknown>): Promise<{ bank_rule: BankRuleData }> {
    const oid = await this.orgId()
    return this.request(`/organisations/${oid}/bank_rules`, { method: 'POST', body: JSON.stringify(data) })
  }

  async updateBankRule(id: string, data: Record<string, unknown>): Promise<{ bank_rule: BankRuleData }> {
    const oid = await this.orgId()
    return this.request(`/organisations/${oid}/bank_rules/${id}`, { method: 'PATCH', body: JSON.stringify(data) })
  }

  async deleteBankRule(id: string): Promise<void> {
    const oid = await this.orgId()
    await this.request(`/organisations/${oid}/bank_rules/${id}`, { method: 'DELETE' })
  }

  async applyBankRules(accountId?: string): Promise<{ matched: number }> {
    const oid = await this.orgId()
    const body = accountId ? JSON.stringify({ account_id: accountId }) : undefined
    return this.request(`/organisations/${oid}/bank_rules/apply`, { method: 'POST', body })
  }

  // ─── Bank Transactions ───────────────────────────────────

  async getBankTransactions(params?: { account_id?: string; status?: string; from?: string; to?: string }): Promise<{ bank_transactions: BankTransactionData[] }> {
    const oid = await this.orgId()
    const query = new URLSearchParams()
    if (params?.account_id) query.set('account_id', params.account_id)
    if (params?.status) query.set('status', params.status)
    if (params?.from) query.set('from', params.from)
    if (params?.to) query.set('to', params.to)
    const qs = query.toString() ? `?${query.toString()}` : ''
    return this.request(`/organisations/${oid}/bank_transactions${qs}`)
  }

  async importBankTransactions(data: { account_id: string; transactions: Array<Record<string, unknown>> }): Promise<{ imported: number }> {
    const oid = await this.orgId()
    return this.request(`/organisations/${oid}/bank_transactions/import`, { method: 'POST', body: JSON.stringify(data) })
  }

  async matchBankTransaction(id: string, journalEntryId: string): Promise<{ bank_transaction: BankTransactionData }> {
    const oid = await this.orgId()
    return this.request(`/organisations/${oid}/bank_transactions/${id}/match`, { method: 'POST', body: JSON.stringify({ journal_entry_id: journalEntryId }) })
  }

  async unmatchBankTransaction(id: string): Promise<{ bank_transaction: BankTransactionData }> {
    const oid = await this.orgId()
    return this.request(`/organisations/${oid}/bank_transactions/${id}/unmatch`, { method: 'POST' })
  }

  async excludeBankTransaction(id: string): Promise<{ bank_transaction: BankTransactionData }> {
    const oid = await this.orgId()
    return this.request(`/organisations/${oid}/bank_transactions/${id}/exclude`, { method: 'POST' })
  }

  async suggestBankMatches(accountId: string): Promise<{ suggestions: unknown[] }> {
    const oid = await this.orgId()
    return this.request(`/organisations/${oid}/bank_transactions/suggest?account_id=${accountId}`)
  }

  // ─── Contact Groups ──────────────────────────────────────

  async getContactGroups(): Promise<{ contact_groups: ContactGroupData[] }> {
    const oid = await this.orgId()
    return this.request(`/organisations/${oid}/contact_groups`)
  }

  async createContactGroup(data: Record<string, unknown>): Promise<{ contact_group: ContactGroupData }> {
    const oid = await this.orgId()
    return this.request(`/organisations/${oid}/contact_groups`, { method: 'POST', body: JSON.stringify(data) })
  }

  async getContactGroup(id: string): Promise<{ contact_group: ContactGroupData & { members: { id: string; contact_type: string; contact_id: string }[] } }> {
    const oid = await this.orgId()
    return this.request(`/organisations/${oid}/contact_groups/${id}`)
  }

  async addContactGroupMember(groupId: string, data: { contact_type: string; contact_id: string }): Promise<unknown> {
    const oid = await this.orgId()
    return this.request(`/organisations/${oid}/contact_groups/${groupId}/members`, { method: 'POST', body: JSON.stringify(data) })
  }

  async removeContactGroupMember(groupId: string, membershipId: string): Promise<void> {
    const oid = await this.orgId()
    await this.request<void>(`/organisations/${oid}/contact_groups/${groupId}/members/${membershipId}`, { method: 'DELETE' })
  }

  // ─── Statements ──────────────────────────────────────────

  async getCustomerStatement(customerId: string, params: { from: string; to: string }): Promise<unknown> {
    const oid = await this.orgId()
    return this.request(`/organisations/${oid}/statements/customer/${customerId}?from=${params.from}&to=${params.to}`)
  }

  async getRemittanceAdvice(supplierId: string, params: { from: string; to: string }): Promise<unknown> {
    const oid = await this.orgId()
    return this.request(`/organisations/${oid}/statements/remittance/${supplierId}?from=${params.from}&to=${params.to}`)
  }

  // ─── Advanced Reports ────────────────────────────────────

  async getBudgetVsActual(budgetId: string, params?: { currency?: string }): Promise<BudgetVsActualReport> {
    const oid = await this.orgId()
    const query = new URLSearchParams({ budget_id: budgetId })
    if (params?.currency) query.set('currency', params.currency)
    return this.request(`/organisations/${oid}/reports/budget_vs_actual?${query.toString()}`)
  }

  async getComparativeProfitLoss(params: { from: string; to: string; compare?: string; currency?: string }): Promise<unknown> {
    const oid = await this.orgId()
    const query = new URLSearchParams({ from: params.from, to: params.to })
    if (params.compare) query.set('compare', params.compare)
    if (params.currency) query.set('currency', params.currency)
    return this.request(`/organisations/${oid}/reports/comparative_profit_loss?${query.toString()}`)
  }

  async getJobProfitability(params?: { status?: string; job_id?: string }): Promise<JobProfitabilityReport> {
    const oid = await this.orgId()
    const query = new URLSearchParams()
    if (params?.status) query.set('status', params.status)
    if (params?.job_id) query.set('job_id', params.job_id)
    const qs = query.toString() ? `?${query.toString()}` : ''
    return this.request(`/organisations/${oid}/reports/job_profitability${qs}`)
  }

  async getAccountTransactions(params: { account_id: string; from: string; to: string; currency?: string }): Promise<AccountTransactionsReport> {
    const oid = await this.orgId()
    const query = new URLSearchParams({ account_id: params.account_id, from: params.from, to: params.to })
    if (params.currency) query.set('currency', params.currency)
    return this.request(`/organisations/${oid}/reports/account_transactions?${query.toString()}`)
  }

  async getExecutiveDashboard(params?: { currency?: string }): Promise<ExecutiveDashboardReport> {
    const oid = await this.orgId()
    const query = new URLSearchParams()
    if (params?.currency) query.set('currency', params.currency)
    const qs = query.toString() ? `?${query.toString()}` : ''
    return this.request(`/organisations/${oid}/reports/executive_dashboard${qs}`)
  }

  async exportReportCsv(reportName: string, params: Record<string, string>): Promise<Blob> {
    const oid = await this.orgId()
    const query = new URLSearchParams({ report_name: reportName, ...params })
    const url = `${BASE}/organisations/${oid}/reports/export_csv?${query.toString()}`
    const token = this.getToken()
    const headers: Record<string, string> = {}
    if (token) headers['Authorization'] = `Bearer ${token}`
    const res = await fetch(url, { headers })
    if (!res.ok) throw new ApiError(res.status, { message: 'Export failed' })
    return res.blob()
  }

  async exportStandardReportPdf(params: { from: string; to: string; as_of: string; currency?: string }): Promise<Blob> {
    const oid = await this.orgId()
    const query = new URLSearchParams(Object.fromEntries(Object.entries(params).filter(([, v]) => v != null)) as Record<string, string>)
    const url = `${BASE}/organisations/${oid}/reports/standard_report_pdf?${query.toString()}`
    const token = this.getToken()
    const headers: Record<string, string> = {}
    if (token) headers['Authorization'] = `Bearer ${token}`
    const res = await fetch(url, { headers })
    if (!res.ok) throw new ApiError(res.status, { message: 'PDF export failed' })
    return res.blob()
  }

  // ─── Personal Org Limits (site admin) ────────────────────

  async getPersonalLimits(): Promise<{ personal_limits: Record<string, number | boolean>; defaults: Record<string, number | boolean> }> {
    return this.request('/admin/personal_limits')
  }

  async updatePersonalLimits(params: Record<string, number | boolean>): Promise<{ personal_limits: Record<string, number | boolean>; message: string }> {
    return this.request('/admin/personal_limits', { method: 'PATCH', body: JSON.stringify(params) })
  }

  // ─── Time Entries ───────────────────────────────────────

  async getTimeEntries(params?: { job_id?: string; user_id?: string; status?: string; start_date?: string; end_date?: string; page?: number; per_page?: number }): Promise<TimeEntriesResponse> {
    const oid = await this.orgId()
    const query = new URLSearchParams()
    if (params?.job_id) query.set('job_id', params.job_id)
    if (params?.user_id) query.set('user_id', params.user_id)
    if (params?.status) query.set('status', params.status)
    if (params?.start_date) query.set('start_date', params.start_date)
    if (params?.end_date) query.set('end_date', params.end_date)
    if (params?.page) query.set('page', params.page.toString())
    if (params?.per_page) query.set('per_page', params.per_page.toString())
    const qs = query.toString() ? `?${query.toString()}` : ''
    return this.request<TimeEntriesResponse>(`/organisations/${oid}/time_entries${qs}`)
  }

  async createTimeEntry(params: { job_id: string; date: string; hours: number; description: string; billable?: boolean; user_id?: string }): Promise<{ time_entry: TimeEntry }> {
    const oid = await this.orgId()
    return this.request<{ time_entry: TimeEntry }>(`/organisations/${oid}/time_entries`, {
      method: 'POST',
      body: JSON.stringify(params),
    })
  }

  async updateTimeEntry(id: string, params: Partial<{ date: string; hours: number; description: string; billable: boolean }>): Promise<{ time_entry: TimeEntry }> {
    const oid = await this.orgId()
    return this.request<{ time_entry: TimeEntry }>(`/organisations/${oid}/time_entries/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(params),
    })
  }

  async deleteTimeEntry(id: string): Promise<void> {
    const oid = await this.orgId()
    await this.request<void>(`/organisations/${oid}/time_entries/${id}`, { method: 'DELETE' })
  }

  async approveTimeEntry(id: string): Promise<{ time_entry: TimeEntry }> {
    const oid = await this.orgId()
    return this.request<{ time_entry: TimeEntry }>(`/organisations/${oid}/time_entries/${id}/approve`, { method: 'POST' })
  }

  async billTimeEntries(params: { invoice_id: string; time_entry_ids: string[]; mode: 'per_entry' | 'grouped' }): Promise<{ billed: boolean; line_items_created: number; entries_billed: number }> {
    const oid = await this.orgId()
    return this.request(`/organisations/${oid}/time_entries/bill`, {
      method: 'POST',
      body: JSON.stringify(params),
    })
  }

  async updateMemberHourlyRate(userId: string, rateCents: number): Promise<{ user: User }> {
    const oid = await this.orgId()
    return this.request<{ user: User }>(`/organisations/${oid}/users/${userId}`, {
      method: 'PATCH',
      body: JSON.stringify({ user: { default_hourly_rate_cents: rateCents } }),
    })
  }

  // ─── Customer Portal (public, no auth) ──────────────────

  async getPortalDashboard(token: string): Promise<PortalDashboard> {
    return this.request<PortalDashboard>(`/portal/${token}`)
  }

  async getPortalInvoice(token: string, invoiceId: string): Promise<PortalInvoiceDetail> {
    return this.request<PortalInvoiceDetail>(`/portal/${token}/invoices/${invoiceId}`)
  }

  async getPortalQuote(token: string, quoteId: string): Promise<PortalQuoteDetail> {
    return this.request<PortalQuoteDetail>(`/portal/${token}/quotes/${quoteId}`)
  }

  async getPortalCreditNote(token: string, creditNoteId: string): Promise<PortalCreditNoteDetail> {
    return this.request<PortalCreditNoteDetail>(`/portal/${token}/credit_notes/${creditNoteId}`)
  }

  async portalPayInvoice(token: string, invoiceId: string, params?: { amount_cents?: number; payment_method?: string }): Promise<PortalPayInvoiceResponse> {
    return this.request(`/portal/${token}/invoices/${invoiceId}/pay`, {
      method: 'POST',
      body: params ? JSON.stringify(params) : undefined,
    })
  }

  async portalAcceptQuote(token: string, quoteId: string): Promise<{ status: string; quote_number: string }> {
    return this.request(`/portal/${token}/quotes/${quoteId}/accept`, { method: 'POST' })
  }

  async portalDeclineQuote(token: string, quoteId: string): Promise<{ status: string; quote_number: string }> {
    return this.request(`/portal/${token}/quotes/${quoteId}/decline`, { method: 'POST' })
  }

  async portalCheckPayment(token: string, invoiceId: string): Promise<{ status: string; paid: boolean }> {
    return this.request(`/portal/${token}/invoices/${invoiceId}/check_payment`)
  }

  async portalSurchargePreview(token: string, invoiceId: string, amountCents: number, method: string): Promise<PortalSurchargePreview> {
    const query = new URLSearchParams({ amount_cents: amountCents.toString(), method })
    return this.request(`/portal/${token}/invoices/${invoiceId}/surcharge_preview?${query}`)
  }

  async portalApplyCredit(token: string, invoiceId: string, creditNoteId: string, amountCents: number): Promise<PortalApplyCreditResponse> {
    return this.request(`/portal/${token}/invoices/${invoiceId}/apply_credit`, {
      method: 'POST',
      body: JSON.stringify({ credit_note_id: creditNoteId, amount_cents: amountCents }),
    })
  }

  // ─── Utility Accounts ────────────────────────────────────

  async getUtilityAccounts(params?: { status?: string; pricing_model?: string }): Promise<{ utility_accounts: UtilityAccount[]; pagination: { page: number; per_page: number; total: number; total_pages: number } }> {
    const oid = await this.orgId()
    const query = new URLSearchParams()
    if (params?.status) query.set('status', params.status)
    if (params?.pricing_model) query.set('pricing_model', params.pricing_model)
    const qs = query.toString() ? `?${query.toString()}` : ''
    return this.request(`/organisations/${oid}/utility_accounts${qs}`)
  }

  async getUtilityAccount(id: string): Promise<UtilityAccountDetail> {
    const oid = await this.orgId()
    return this.request(`/organisations/${oid}/utility_accounts/${id}`)
  }

  async createUtilityAccount(data: Record<string, unknown>): Promise<UtilityAccountDetail> {
    const oid = await this.orgId()
    return this.request(`/organisations/${oid}/utility_accounts`, { method: 'POST', body: JSON.stringify(data) })
  }

  async updateUtilityAccount(id: string, data: Record<string, unknown>): Promise<UtilityAccountDetail> {
    const oid = await this.orgId()
    return this.request(`/organisations/${oid}/utility_accounts/${id}`, { method: 'PATCH', body: JSON.stringify(data) })
  }

  async deleteUtilityAccount(id: string): Promise<UtilityAccount> {
    const oid = await this.orgId()
    return this.request(`/organisations/${oid}/utility_accounts/${id}`, { method: 'DELETE' })
  }

  async getUtilityProjection(utilityAccountId: string): Promise<BillProjection> {
    const oid = await this.orgId()
    return this.request(`/organisations/${oid}/utility_accounts/${utilityAccountId}/projection`)
  }

  async getUtilityRateHistory(utilityAccountId: string): Promise<{ rate_plans: UtilityRatePlan[] }> {
    const oid = await this.orgId()
    return this.request(`/organisations/${oid}/utility_accounts/${utilityAccountId}/rate_history`)
  }

  async updateUtilityRates(utilityAccountId: string, data: Record<string, unknown>): Promise<UtilityRatePlan> {
    const oid = await this.orgId()
    return this.request(`/organisations/${oid}/utility_accounts/${utilityAccountId}/update_rates`, { method: 'POST', body: JSON.stringify(data) })
  }

  // ─── Utility Readings ────────────────────────────────────

  async getUtilityReadings(utilityAccountId: string, params?: { from?: string; to?: string }): Promise<{ utility_readings: UtilityReading[]; pagination: { page: number; per_page: number; total: number; total_pages: number } }> {
    const oid = await this.orgId()
    const query = new URLSearchParams()
    if (params?.from) query.set('from', params.from)
    if (params?.to) query.set('to', params.to)
    const qs = query.toString() ? `?${query.toString()}` : ''
    return this.request(`/organisations/${oid}/utility_accounts/${utilityAccountId}/readings${qs}`)
  }

  async getUtilityReading(utilityAccountId: string, readingId: string): Promise<UtilityReadingDetail> {
    const oid = await this.orgId()
    return this.request(`/organisations/${oid}/utility_accounts/${utilityAccountId}/readings/${readingId}`)
  }

  async createUtilityReading(utilityAccountId: string, data: Record<string, unknown>): Promise<{ utility_reading: UtilityReadingDetail; warnings: Array<{ key: string; message: string }> }> {
    const oid = await this.orgId()
    return this.request(`/organisations/${oid}/utility_accounts/${utilityAccountId}/readings`, { method: 'POST', body: JSON.stringify(data) })
  }

  async updateUtilityReading(utilityAccountId: string, readingId: string, data: Record<string, unknown>): Promise<{ utility_reading: UtilityReadingDetail; warnings: Array<{ key: string; message: string }> }> {
    const oid = await this.orgId()
    return this.request(`/organisations/${oid}/utility_accounts/${utilityAccountId}/readings/${readingId}`, { method: 'PATCH', body: JSON.stringify(data) })
  }

  async deleteUtilityReading(utilityAccountId: string, readingId: string): Promise<void> {
    const oid = await this.orgId()
    return this.request(`/organisations/${oid}/utility_accounts/${utilityAccountId}/readings/${readingId}`, { method: 'DELETE' })
  }

  async getUtilityReconciliation(utilityAccountId: string, readingId: string): Promise<BillReconciliation> {
    const oid = await this.orgId()
    return this.request(`/organisations/${oid}/utility_accounts/${utilityAccountId}/readings/${readingId}/reconciliation`)
  }

  // ─── Spot Prices ─────────────────────────────────────────

  async getSpotPrices(params?: { grid_zone_id?: number; from?: string; to?: string }): Promise<{ spot_prices: SpotPrice[] }> {
    const oid = await this.orgId()
    const query = new URLSearchParams()
    if (params?.grid_zone_id) query.set('grid_zone_id', params.grid_zone_id.toString())
    if (params?.from) query.set('from', params.from)
    if (params?.to) query.set('to', params.to)
    const qs = query.toString() ? `?${query.toString()}` : ''
    return this.request(`/organisations/${oid}/spot_prices${qs}`)
  }

  async getCurrentSpotPrices(): Promise<{ spot_prices: GridZone[] }> {
    const oid = await this.orgId()
    return this.request(`/organisations/${oid}/spot_prices/current`)
  }

  // ─── Feature Flags (site-admin) ──────────────────────────

  async getFeatureFlags(): Promise<{ feature_flags: FeatureFlag[] }> {
    return this.request('/feature_flags')
  }

  async getFeatureFlag(id: string): Promise<FeatureFlag> {
    return this.request(`/feature_flags/${id}`)
  }

  async createFeatureFlag(params: {
    name: string
    description?: string
    enabled?: boolean
    channel?: ReleaseChannel
    metadata?: Record<string, unknown>
  }): Promise<FeatureFlag> {
    return this.request('/feature_flags', {
      method: 'POST',
      body: JSON.stringify(params),
    })
  }

  async updateFeatureFlag(id: string, params: {
    name?: string
    description?: string
    enabled?: boolean
    channel?: ReleaseChannel
    geo_countries?: string
    metadata?: Record<string, unknown>
  }): Promise<FeatureFlag> {
    return this.request(`/feature_flags/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(params),
    })
  }

  async deleteFeatureFlag(id: string): Promise<void> {
    return this.request(`/feature_flags/${id}`, { method: 'DELETE' })
  }

  async getFeatureFlagOverrides(flagId: string): Promise<{ feature_flag_id: string; overrides: FeatureFlagOverride[] }> {
    return this.request(`/feature_flags/${flagId}/overrides`)
  }

  async createFeatureFlagOverride(flagId: string, params: {
    organisation_id: string
    enabled: boolean
  }): Promise<FeatureFlagOverride> {
    return this.request(`/feature_flags/${flagId}/overrides`, {
      method: 'POST',
      body: JSON.stringify(params),
    })
  }

  async deleteFeatureFlagOverride(flagId: string, overrideId: string): Promise<void> {
    return this.request(`/feature_flags/${flagId}/overrides/${overrideId}`, { method: 'DELETE' })
  }

  async getOrgFeatures(): Promise<OrgFeaturesResponse> {
    const oid = await this.orgId()
    return this.request(`/organisations/${oid}/features`)
  }

  // ─── MYOB Imports ───────────────────────────────────────────

  async uploadMyoxFile(orgId: string, file: File): Promise<{ status: string; blob_id: string }> {
    const url = `${BASE}/organisations/${orgId}/imports`
    const token = this.getToken()
    const form = new FormData()
    form.append('file', file)
    const headers: Record<string, string> = {}
    if (token) headers['Authorization'] = `Bearer ${token}`
    const res = await fetch(url, { method: 'POST', headers, body: form })
    if (!res.ok) {
      const data = await res.json().catch(() => ({ message: res.statusText }))
      throw new ApiError(res.status, data)
    }
    return res.json()
  }

  async getImportStatus(orgId: string, importId: string): Promise<ImportStatus> {
    const data = await this.request<{ import: ImportStatus }>(`/organisations/${orgId}/imports/${importId}`)
    return data.import
  }

  async confirmImport(orgId: string, importId: string): Promise<ImportStatus> {
    const data = await this.request<{ import: ImportStatus }>(`/organisations/${orgId}/imports/${importId}/confirm`, {
      method: 'POST',
    })
    return data.import
  }

  async cancelImport(orgId: string, importId: string): Promise<void> {
    await this.request(`/organisations/${orgId}/imports/${importId}/cancel`, { method: 'POST' })
  }

  async updateOrgReleaseChannel(orgId: string, releaseChannel: ReleaseChannel): Promise<AdminOrganisation> {
    return this.request(`/admin/organisations/${orgId}/release_channel`, {
      method: 'PATCH',
      body: JSON.stringify({ release_channel: releaseChannel }),
    })
  }

  // ─── Geo Restriction ────────────────────────────────────────

  async getGeoSettings(): Promise<GeoRestrictionSettings> {
    return this.request<GeoRestrictionSettings>('/settings/geo')
  }

  async updateGeoSettings(settings: {
    enabled?: boolean
    mode?: 'whitelist' | 'blacklist'
    countries?: string[]
  }): Promise<{ updated: boolean; message: string; settings: GeoRestrictionSettings }> {
    return this.request<{ updated: boolean; message: string; settings: GeoRestrictionSettings }>('/settings/geo', {
      method: 'PUT',
      body: JSON.stringify(settings),
    })
  }

  // ─── V2 Helpers ────────────────────────────────────────────

  async requestV2<T>(path: string, options?: RequestInit): Promise<T> {
    return this.request<T>(path, options, 'v2')
  }

  private get orgPath(): string {
    const orgId = this.currentOrgId
    if (!orgId) throw new Error('No organisation selected')
    return `/organisations/${orgId}`
  }

  // ─── Inbound Bill Captures (V2) ───────────────────────────

  async getCaptures(params?: {
    status?: CaptureStatus
    source?: CaptureSource
    page?: number
    per_page?: number
  }): Promise<CapturesResponse> {
    const qs = new URLSearchParams()
    if (params?.status) qs.set('status', params.status)
    if (params?.source) qs.set('source', params.source)
    if (params?.page) qs.set('page', String(params.page))
    if (params?.per_page) qs.set('per_page', String(params.per_page))
    const q = qs.toString()
    return this.requestV2<CapturesResponse>(`${this.orgPath}/captures${q ? `?${q}` : ''}`)
  }

  async getCapture(id: string): Promise<InboundBillCaptureDetail> {
    return this.requestV2<InboundBillCaptureDetail>(`${this.orgPath}/captures/${id}`)
  }

  async updateCapture(id: string, params: {
    extracted_data?: Record<string, unknown>
    supplier_id?: string
    potential_supplier_id?: string
  }): Promise<InboundBillCaptureDetail> {
    return this.requestV2<InboundBillCaptureDetail>(`${this.orgPath}/captures/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(params),
    })
  }

  async confirmCapture(id: string, data: {
    supplier_id?: string
    bill_number?: string
    bill_date?: string
    due_at?: string
    currency?: string
    notes?: string
    total_cents?: number
    tax_cents?: number
    tax_rate?: number
    amounts_tax_inclusive?: boolean
    zero_tax_reason?: string
    line_items?: Array<{
      description: string
      quantity: number
      unit_price: number
      amount: number
      tax_rate?: number
    }>
  }): Promise<{ message: string; capture: InboundBillCapture; bill: { id: string; bill_number: string; status: string } }> {
    return this.requestV2(`${this.orgPath}/captures/${id}/confirm`, {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  async reprocessCapture(id: string): Promise<{ message: string; capture: InboundBillCapture }> {
    return this.requestV2(`${this.orgPath}/captures/${id}/reprocess`, {
      method: 'POST',
    })
  }

  async dismissCapture(id: string): Promise<void> {
    return this.requestV2<void>(`${this.orgPath}/captures/${id}`, {
      method: 'DELETE',
    })
  }

  async extractBill(file: File): Promise<{ capture_id: string; bill_id: string; queue_position: number }> {
    const formData = new FormData()
    formData.append('file', file)
    return this.requestV2(`${this.orgPath}/bills/extract`, {
      method: 'POST',
      body: formData,
    })
  }

  // ─── Tax Filing (V2) ──────────────────────────────────────

  async getTaxFilingWaiverStatus(): Promise<{
    accepted: boolean
    jurisdiction: string
    current_version: string
    waiver_text: string | null
  }> {
    return this.requestV2(`${this.orgPath}/tax_filing/waiver_status`)
  }

  async acceptTaxFilingWaiver(): Promise<{ accepted: boolean; jurisdiction: string }> {
    return this.requestV2(`${this.orgPath}/tax_filing/accept_waiver`, { method: 'POST' })
  }

  async getTaxFilingPeriods(year?: number): Promise<{
    periods: Array<{
      period_start: string
      period_end: string
      due_date: string
      filed: boolean
      locked: boolean
      status: 'filed' | 'ready' | 'open'
    }>
    frequency: string | null
    year: number
  }> {
    const qs = year ? `?year=${year}` : ''
    return this.requestV2(`${this.orgPath}/tax_filing/periods${qs}`)
  }

  async calculateTaxFiling(periodStart: string, periodEnd: string): Promise<{
    output_gst_cents: number
    input_gst_cents: number
    net_gst_cents: number
    sales_total_cents: number
    purchases_total_cents: number
    basis: string
    period_start: string
    period_end: string
    due_date: string | null
    line_items: Record<string, { sales_net: number; sales_tax: number; purchases_net: number; purchases_tax: number }>
    warnings: string[]
    unmatched_bills: Array<{ id: string; bill_number: string; supplier: string | null; total_cents: number }>
  }> {
    return this.requestV2(`${this.orgPath}/tax_filing/calculate?period_start=${periodStart}&period_end=${periodEnd}`)
  }

  async updateTaxFilingSettings(settings: {
    gst_filing_basis?: string
    gst_filing_frequency?: string
    gst_filing_group?: string
    gst_filing_balance_month?: number
    gst_filing_reminders?: boolean
  }): Promise<Record<string, unknown>> {
    return this.requestV2(`${this.orgPath}/tax_filing/settings`, {
      method: 'PATCH',
      body: JSON.stringify(settings),
    })
  }

  async getTaxFilingSettings(): Promise<{
    gst_filing_basis: string
    gst_filing_frequency: string | null
    gst_filing_group: string | null
    gst_filing_balance_month: number | null
    gst_filing_reminders: boolean
  }> {
    return this.requestV2(`${this.orgPath}/tax_filing/settings`)
  }

  // ─── V3 Helpers ────────────────────────────────────────────

  async requestV3<T>(path: string, options?: RequestInit): Promise<T> {
    return this.request<T>(path, options, 'v3')
  }

  // ─── Tax Profiles (V3) ────────────────────────────────────

  async getTaxProfile(): Promise<TaxProfileResponse | null> {
    try {
      return await this.requestV3<TaxProfileResponse>(`${this.orgPath}/tax_profiles/current`)
    } catch (e) {
      if (e instanceof ApiError && e.status === 404) return null
      throw e
    }
  }

  async createTaxProfile(params: {
    entity_type: string
    jurisdiction: string
    tax_year_start_month?: number
  }): Promise<TaxProfileResponse> {
    return this.requestV3(`${this.orgPath}/tax_profiles`, {
      method: 'POST',
      body: JSON.stringify(params),
    })
  }

  async updateTaxProfile(params: Record<string, unknown>): Promise<TaxProfileResponse> {
    return this.requestV3(`${this.orgPath}/tax_profiles/current`, {
      method: 'PATCH',
      body: JSON.stringify(params),
    })
  }

  // ─── Tax Periods (V3) ─────────────────────────────────────

  async getTaxPeriods(params?: {
    period_type?: string
    page?: number
    per_page?: number
  }): Promise<TaxPeriodsResponse> {
    const qs = new URLSearchParams()
    if (params?.period_type) qs.set('period_type', params.period_type)
    if (params?.page) qs.set('page', String(params.page))
    if (params?.per_page) qs.set('per_page', String(params.per_page))
    const q = qs.toString()
    return this.requestV3(`${this.orgPath}/tax_periods${q ? `?${q}` : ''}`)
  }

  async generateTaxPeriods(): Promise<{ generated: number }> {
    return this.requestV3(`${this.orgPath}/tax_periods/generate`, { method: 'POST' })
  }

  // ─── Tax Adjustments (V3) ─────────────────────────────────

  async getTaxAdjustments(params?: {
    tax_period_id?: string
    category?: string
    source?: string
    page?: number
  }): Promise<TaxAdjustmentsResponse> {
    const qs = new URLSearchParams()
    if (params?.tax_period_id) qs.set('tax_period_id', params.tax_period_id)
    if (params?.category) qs.set('category', params.category)
    if (params?.source) qs.set('source', params.source)
    if (params?.page) qs.set('page', String(params.page))
    const q = qs.toString()
    return this.requestV3(`${this.orgPath}/tax_adjustments${q ? `?${q}` : ''}`)
  }

  async createTaxAdjustment(params: {
    tax_period_id: string
    category: string
    description: string
    amount_cents: number
    currency: string
    notes?: string
  }): Promise<TaxAdjustmentItem> {
    return this.requestV3(`${this.orgPath}/tax_adjustments`, {
      method: 'POST',
      body: JSON.stringify(params),
    })
  }

  async updateTaxAdjustment(id: string, params: Record<string, unknown>): Promise<TaxAdjustmentItem> {
    return this.requestV3(`${this.orgPath}/tax_adjustments/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(params),
    })
  }

  async deleteTaxAdjustment(id: string): Promise<void> {
    return this.requestV3(`${this.orgPath}/tax_adjustments/${id}`, { method: 'DELETE' })
  }

  // ─── Apportionment (V3) ───────────────────────────────────

  async getApportionmentProfiles(): Promise<ApportionmentProfilesResponse> {
    return this.requestV3(`${this.orgPath}/apportionment_profiles`)
  }

  async createApportionmentProfile(params: {
    name: string
    method_type: string
    percentage: number
    description?: string
  }): Promise<ApportionmentProfileItem> {
    return this.requestV3(`${this.orgPath}/apportionment_profiles`, {
      method: 'POST',
      body: JSON.stringify(params),
    })
  }

  async updateApportionmentProfile(id: string, params: Record<string, unknown>): Promise<ApportionmentProfileItem> {
    return this.requestV3(`${this.orgPath}/apportionment_profiles/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(params),
    })
  }

  async deleteApportionmentProfile(id: string): Promise<void> {
    return this.requestV3(`${this.orgPath}/apportionment_profiles/${id}`, { method: 'DELETE' })
  }

  async calculateApportionment(id: string): Promise<{ profile_id: string; percentage: number; method: string }> {
    return this.requestV3(`${this.orgPath}/apportionment_profiles/${id}/calculate`)
  }

  // ─── Income Tax (V3) ──────────────────────────────────────

  async getIncomeTaxComputations(params?: { page?: number }): Promise<IncomeTaxComputationsResponse> {
    const qs = params?.page ? `?page=${params.page}` : ''
    return this.requestV3(`${this.orgPath}/income_tax_computations${qs}`)
  }

  async computeIncomeTax(taxPeriodId: string): Promise<IncomeTaxComputationItem> {
    return this.requestV3(`${this.orgPath}/income_tax_computations/compute`, {
      method: 'POST',
      body: JSON.stringify({ tax_period_id: taxPeriodId }),
    })
  }

  async finalizeIncomeTax(id: string): Promise<IncomeTaxComputationItem> {
    return this.requestV3(`${this.orgPath}/income_tax_computations/${id}/finalize`, { method: 'POST' })
  }

  // ─── Provisional Tax (V3) ─────────────────────────────────

  async getProvisionalTaxEstimates(params?: { tax_period_id?: string }): Promise<ProvisionalTaxEstimatesResponse> {
    const qs = params?.tax_period_id ? `?tax_period_id=${params.tax_period_id}` : ''
    return this.requestV3(`${this.orgPath}/provisional_tax_estimates${qs}`)
  }

  async computeProvisionalTax(taxPeriodId: string): Promise<{ estimates: ProvisionalTaxEstimateItem[] }> {
    return this.requestV3(`${this.orgPath}/provisional_tax_estimates/compute`, {
      method: 'POST',
      body: JSON.stringify({ tax_period_id: taxPeriodId }),
    })
  }

  async recordProvisionalPayment(id: string, amountCents: number): Promise<ProvisionalTaxEstimateItem> {
    return this.requestV3(`${this.orgPath}/provisional_tax_estimates/${id}/record_payment`, {
      method: 'POST',
      body: JSON.stringify({ amount_cents: amountCents }),
    })
  }

  // ─── Employee Deductions (V3 / AU only) ───────────────────

  async getEmployeeDeductions(params?: {
    tax_period_id?: string
    category_id?: string
    page?: number
  }): Promise<EmployeeDeductionsResponse> {
    const qs = new URLSearchParams()
    if (params?.tax_period_id) qs.set('tax_period_id', params.tax_period_id)
    if (params?.category_id) qs.set('category_id', params.category_id)
    if (params?.page) qs.set('page', String(params.page))
    const q = qs.toString()
    return this.requestV3(`${this.orgPath}/employee_deductions${q ? `?${q}` : ''}`)
  }

  async createEmployeeDeduction(params: {
    tax_period_id: string
    category_id: string
    description: string
    amount_cents: number
    currency: string
    mixed_use?: boolean
    apportionment_percentage?: number
    receipt_attached?: boolean
    incurred_on?: string
    notes?: string
  }): Promise<EmployeeDeductionItem & { warnings?: Array<{ warning: string; severity: string; message: string }> }> {
    return this.requestV3(`${this.orgPath}/employee_deductions`, {
      method: 'POST',
      body: JSON.stringify(params),
    })
  }

  async updateEmployeeDeduction(id: string, params: Record<string, unknown>): Promise<EmployeeDeductionItem> {
    return this.requestV3(`${this.orgPath}/employee_deductions/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(params),
    })
  }

  async deleteEmployeeDeduction(id: string): Promise<void> {
    return this.requestV3(`${this.orgPath}/employee_deductions/${id}`, { method: 'DELETE' })
  }

  async getDeductionCategories(): Promise<{ categories: DeductionCategoryItem[] }> {
    return this.requestV3(`${this.orgPath}/employee_deductions/categories`)
  }

  async getDeductionSummary(taxPeriodId: string): Promise<DeductionSummaryResponse> {
    return this.requestV3(`${this.orgPath}/employee_deductions/summary?tax_period_id=${taxPeriodId}`)
  }

  // ─── Tax Position Snapshot (V3) ───────────────────────────

  async getTaxPositionSnapshot(params?: {
    tax_period_id?: string
    snapshot_type?: string
  }): Promise<TaxPositionSnapshotItem | null> {
    try {
      const qs = new URLSearchParams()
      if (params?.tax_period_id) qs.set('tax_period_id', params.tax_period_id)
      if (params?.snapshot_type) qs.set('snapshot_type', params.snapshot_type)
      const q = qs.toString()
      return await this.requestV3<TaxPositionSnapshotItem>(`${this.orgPath}/tax_position_snapshot${q ? `?${q}` : ''}`)
    } catch (e) {
      if (e instanceof ApiError && e.status === 404) return null
      throw e
    }
  }

  async generateTaxPositionSnapshot(taxPeriodId: string): Promise<TaxPositionSnapshotItem> {
    return this.requestV3(`${this.orgPath}/tax_position_snapshot/generate`, {
      method: 'POST',
      body: JSON.stringify({ tax_period_id: taxPeriodId }),
    })
  }

  // ─── Filing Packs (V3) ────────────────────────────────────

  async getFilingPacks(params?: {
    tax_period_id?: string
    pack_type?: string
    status?: string
    page?: number
  }): Promise<FilingPacksResponse> {
    const qs = new URLSearchParams()
    if (params?.tax_period_id) qs.set('tax_period_id', params.tax_period_id)
    if (params?.pack_type) qs.set('pack_type', params.pack_type)
    if (params?.status) qs.set('status', params.status)
    if (params?.page) qs.set('page', String(params.page))
    const q = qs.toString()
    return this.requestV3(`${this.orgPath}/filing_packs${q ? `?${q}` : ''}`)
  }

  async getFilingPack(id: string): Promise<FilingPackDetailItem> {
    return this.requestV3(`${this.orgPath}/filing_packs/${id}`)
  }

  async createFilingPack(params: {
    tax_period_id: string
    pack_type: string
    snapshot_id?: string
  }): Promise<FilingPackDetailItem> {
    return this.requestV3(`${this.orgPath}/filing_packs`, {
      method: 'POST',
      body: JSON.stringify(params),
    })
  }

  async approveFilingPack(id: string): Promise<FilingPackItem> {
    return this.requestV3(`${this.orgPath}/filing_packs/${id}/approve`, { method: 'POST' })
  }

  async downloadFilingPack(id: string, format?: 'pdf' | 'json'): Promise<FilingPackExport | Blob> {
    const qs = format ? `?format=${format}` : ''
    return this.requestV3(`${this.orgPath}/filing_packs/${id}/download${qs}`)
  }

  // ─── Tax Calendar (V3) ────────────────────────────────────

  async getTaxCalendar(monthsAhead?: number): Promise<TaxCalendarResponse> {
    const qs = monthsAhead ? `?months_ahead=${monthsAhead}` : ''
    return this.requestV3(`${this.orgPath}/tax_calendar${qs}`)
  }

  // ─── V3 Income Tax Waiver ─────────────────────────────────

  async getIncomeTaxWaiverStatus(): Promise<{
    accepted: boolean
    jurisdiction: string
    current_version: string
    waiver_text: string | null
  }> {
    return this.requestV3(`${this.orgPath}/income_tax/waiver_status`)
  }

  async acceptIncomeTaxWaiver(): Promise<{ accepted: boolean; jurisdiction: string }> {
    return this.requestV3(`${this.orgPath}/income_tax/accept_waiver`, { method: 'POST' })
  }

  // ─── V3 GST Filing (ported) ───────────────────────────────

  async getGstWaiverStatusV3(): Promise<{
    accepted: boolean
    jurisdiction: string
    current_version: string
    waiver_text: string | null
  }> {
    return this.requestV3(`${this.orgPath}/tax_filing/waiver_status`)
  }

  async acceptGstWaiverV3(): Promise<{ accepted: boolean; jurisdiction: string }> {
    return this.requestV3(`${this.orgPath}/tax_filing/accept_waiver`, { method: 'POST' })
  }

  async calculateGstV3(periodStart: string, periodEnd: string): Promise<{
    output_gst_cents: number
    input_gst_cents: number
    net_gst_cents: number
    sales_total_cents: number
    purchases_total_cents: number
    basis: string
    period_start: string
    period_end: string
    due_date: string | null
    line_items: Record<string, { sales_net: number; sales_tax: number; purchases_net: number; purchases_tax: number }>
    warnings: string[]
    unmatched_bills: Array<{ id: string; bill_number: string; supplier: string | null; total_cents: number }>
  }> {
    return this.requestV3(`${this.orgPath}/tax_filing/calculate?period_start=${periodStart}&period_end=${periodEnd}`)
  }

  async getGstPeriodsV3(year?: number): Promise<{
    periods: Array<{
      period_start: string
      period_end: string
      due_date: string
      filed: boolean
      locked: boolean
      status: 'filed' | 'ready' | 'open'
    }>
    frequency: string | null
    year: number
  }> {
    const qs = year ? `?year=${year}` : ''
    return this.requestV3(`${this.orgPath}/tax_filing/periods${qs}`)
  }

  async updateGstSettingsV3(settings: {
    gst_filing_basis?: string
    gst_filing_frequency?: string
    gst_filing_group?: string
    gst_filing_balance_month?: number
    gst_filing_reminders?: boolean
  }): Promise<Record<string, unknown>> {
    return this.requestV3(`${this.orgPath}/tax_filing/settings`, {
      method: 'PATCH',
      body: JSON.stringify(settings),
    })
  }

  async getGstSettingsV3(): Promise<{
    gst_filing_basis: string
    gst_filing_frequency: string | null
    gst_filing_group: string | null
    gst_filing_balance_month: number | null
    gst_filing_reminders: boolean
  }> {
    return this.requestV3(`${this.orgPath}/tax_filing/settings`)
  }
}

export class ApiError extends Error {
  status: number
  data: Record<string, unknown>

  constructor(status: number, data: Record<string, unknown>) {
    super((data.message as string) || (data.error as string) || `API error ${status}`)
    this.status = status
    this.data = data
  }
}

export const api = new ApiClient()
